import React, { useState, useEffect } from 'react';

const VisuTelPreLaunch = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [dashboardMetric, setDashboardMetric] = useState('coverage');
  const [emailInput, setEmailInput] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [show2025Actions, setShow2025Actions] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth < 1024;

  // Smooth scroll function
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Animated counter hook
  const useCounter = (target, duration = 2000) => {
    const [count, setCount] = useState(0);
    useEffect(() => {
      let start = 0;
      const increment = target / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= target) {
          setCount(target);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }, [target, duration]);
    return count;
  };

  const patentCount = useCounter(40);
  const claimCount = useCounter(3200);
  const coveragePercent = useCounter(98);

  // eLoran Coverage Map state
  const [showOption, setShowOption] = useState('full');
  const [showCoverage, setShowCoverage] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [showBaselines, setShowBaselines] = useState(false);
  const [coverageRange, setCoverageRange] = useState(700);
  const [simulatedStatus, setSimulatedStatus] = useState({ networkHealth: 98, gpsHealth: 72, eloranHealth: 99 });

  // Modal state for Privacy and Terms
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Simulate real-time status updates
  useEffect(() => {
    const interval = setInterval(() => {
      setSimulatedStatus({
        networkHealth: 95 + Math.random() * 5,
        gpsHealth: 65 + Math.random() * 20,
        eloranHealth: 97 + Math.random() * 3
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Map projection parameters
  const mapBounds = { minLat: 24, maxLat: 50, minLon: -125, maxLon: -66 };
  const svgWidth = 960;
  const svgHeight = 600;

  const projectLon = (lon) => ((lon - mapBounds.minLon) / (mapBounds.maxLon - mapBounds.minLon)) * svgWidth;
  const projectLat = (lat) => (svgHeight - 100) - ((lat - mapBounds.minLat) / (mapBounds.maxLat - mapBounds.minLat)) * (svgHeight - 100);
  const kmToPixels = (km) => (km / 111) * ((svgHeight - 100) / (mapBounds.maxLat - mapBounds.minLat));

  // Base 15 CONUS stations per Registry v7.25 (4 Pilot + 11 Expansion)
  const baseStations = [
    // Pilot Sites (4)
    { id: 'SENECA', name: 'Seneca', state: 'NY', lat: 42.71, lon: -76.83, phase: 'pilot', chain: 'GL 8970 X-Ray' },
    { id: 'WILDWOOD', name: 'Wildwood', state: 'NJ', lat: 39.00, lon: -74.85, phase: 'pilot', chain: 'NE US 9960' },
    { id: 'CAROLINA', name: 'Carolina Beach', state: 'NC', lat: 34.06, lon: -77.91, phase: 'pilot', chain: 'SE US 7980' },
    { id: 'DANA', name: 'Dana', state: 'IN', lat: 39.85, lon: -87.48, phase: 'pilot', chain: 'GL 8970 Master (HUB)' },
    // CONUS Expansion (11)
    { id: 'MALONE', name: 'Malone', state: 'FL', lat: 30.99, lon: -85.17, phase: 'base', chain: 'Gulf Master' },
    { id: 'RAYVILLE', name: 'Rayville', state: 'LA', lat: 32.48, lon: -91.75, phase: 'base', chain: 'Gulf Central' },
    { id: 'RAYMONDVILLE', name: 'Raymondville', state: 'TX', lat: 26.53, lon: -97.83, phase: 'base', chain: 'Gulf/Southwest' },
    { id: 'SW_MN', name: 'SW Minnesota', state: 'MN', lat: 44.40, lon: -95.80, phase: 'base', chain: 'North Central' },
    { id: 'GEORGE', name: 'George', state: 'WA', lat: 47.07, lon: -119.74, phase: 'base', chain: 'Pacific NW' },
    { id: 'FALLON', name: 'Fallon', state: 'NV', lat: 39.55, lon: -118.83, phase: 'base', chain: 'West Coast' },
    { id: 'SEARCHLIGHT', name: 'Searchlight', state: 'NV', lat: 35.46, lon: -114.92, phase: 'base', chain: 'Southwest' },
    { id: 'HAVRE', name: 'Havre', state: 'MT', lat: 48.54, lon: -109.98, phase: 'base', chain: 'Northern Plains' },
    { id: 'GILLETTE', name: 'Gillette', state: 'WY', lat: 44.00, lon: -105.62, phase: 'base', chain: 'Central US' },
    { id: 'BOISE_CITY', name: 'Boise City', state: 'OK', lat: 36.50, lon: -102.50, phase: 'base', chain: 'Central US' },
    { id: 'LAS_CRUCES', name: 'Las Cruces', state: 'NM', lat: 32.30, lon: -106.78, phase: 'base', chain: 'Southwest' },
  ];

  // Territory stations (5)
  const territoryStations = [
    { id: 'KODIAK', name: 'Kodiak', state: 'AK', lat: 57.73, lon: -152.50, phase: 'territory', chain: 'Alaska' },
    { id: 'PT_CLARENCE', name: 'Port Clarence', state: 'AK', lat: 65.25, lon: -166.86, phase: 'territory', chain: 'Alaska' },
    { id: 'UPOLU_PT', name: 'Upolu Point', state: 'HI', lat: 20.24, lon: -155.88, phase: 'territory', chain: 'Pacific' },
    { id: 'AGUADILLA', name: 'Aguadilla', state: 'PR', lat: 18.43, lon: -67.15, phase: 'territory', chain: 'Caribbean' },
    { id: 'GUAM', name: 'Guam', state: 'GU', lat: 13.45, lon: 144.78, phase: 'territory', chain: 'Indo-Pacific' },
  ];

  const getVisibleStations = () => {
    if (showOption === 'pilot') {
      return baseStations.filter(s => s.phase === 'pilot');
    }
    let stations = [...baseStations];
    if (showOption === 'territories') {
      stations.push(...territoryStations);
    }
    return stations;
  };

  const visibleStations = getVisibleStations();
  const conusStations = visibleStations.filter(s => s.phase !== 'territory');
  const allStations = [...baseStations, ...territoryStations];

  const getBaselines = () => [
    { from: 'DANA', to: 'SENECA', distance: 850, type: 'optimal' },
    { from: 'DANA', to: 'SW_MN', distance: 750, type: 'optimal' },
    { from: 'SW_MN', to: 'HAVRE', distance: 900, type: 'optimal' },
    { from: 'SW_MN', to: 'GILLETTE', distance: 700, type: 'optimal' },
    { from: 'DANA', to: 'HAVRE', distance: 1850, type: 'long' },
  ];

  const getStationColor = (station) => {
    if (station.phase === 'pilot') return '#10b981';
    if (station.phase === 'base') return '#3b82f6';
    if (station.phase === 'territory') return '#f43f5e';
    return '#3b82f6';
  };

  const getBaselineColor = (type) => {
    if (type === 'long') return '#ef4444';
    if (type === 'optimal') return '#22c55e';
    return '#f59e0b';
  };

  const getStats = () => {
    switch(showOption) {
      case 'pilot':
        return { count: 4, metros: '45%', conus: '35%', gap: 'East/Central Core', cost: '$6-12M' };
      case 'full':
        return { count: 15, metros: '99%', conus: '98%', gap: 'Minimal', cost: '$34-56M' };
      case 'territories':
        return { count: 20, metros: '99%', conus: '98%', gap: 'Full National', cost: '$45-70M' };
      default:
        return { count: 15, metros: '99%', conus: '98%', gap: 'Minimal', cost: '$34-56M' };
    }
  };

  const stats = getStats();

  const buttonOptions = [
    { value: 'pilot', label: 'Phase 1 Pilot (4)', color: '#10b981' },
    { value: 'full', label: 'Full CONUS (15)', color: '#3b82f6' },
    { value: 'territories', label: '+ Territories (20)', color: '#f43f5e' },
  ];

  // US outline path
  const usOutlinePath = `
    M ${projectLon(-124.7)} ${projectLat(48.4)}
    L ${projectLon(-123.5)} ${projectLat(46.3)}
    L ${projectLon(-124.2)} ${projectLat(43.0)}
    L ${projectLon(-124.4)} ${projectLat(40.0)}
    L ${projectLon(-120.0)} ${projectLat(34.0)}
    L ${projectLon(-117.1)} ${projectLat(32.5)}
    L ${projectLon(-114.7)} ${projectLat(32.7)}
    L ${projectLon(-111.0)} ${projectLat(31.3)}
    L ${projectLon(-108.2)} ${projectLat(31.3)}
    L ${projectLon(-106.5)} ${projectLat(31.8)}
    L ${projectLon(-104.0)} ${projectLat(29.5)}
    L ${projectLon(-103.0)} ${projectLat(28.9)}
    L ${projectLon(-102.3)} ${projectLat(29.8)}
    L ${projectLon(-99.5)} ${projectLat(27.5)}
    L ${projectLon(-97.4)} ${projectLat(25.9)}
    L ${projectLon(-97.2)} ${projectLat(26.0)}
    L ${projectLon(-97.0)} ${projectLat(27.8)}
    L ${projectLon(-94.5)} ${projectLat(29.3)}
    L ${projectLon(-93.8)} ${projectLat(29.7)}
    L ${projectLon(-91.0)} ${projectLat(29.0)}
    L ${projectLon(-89.0)} ${projectLat(28.9)}
    L ${projectLon(-88.5)} ${projectLat(30.2)}
    L ${projectLon(-87.5)} ${projectLat(30.3)}
    L ${projectLon(-86.5)} ${projectLat(30.4)}
    L ${projectLon(-85.0)} ${projectLat(29.7)}
    L ${projectLon(-83.0)} ${projectLat(29.0)}
    L ${projectLon(-82.5)} ${projectLat(27.5)}
    L ${projectLon(-80.0)} ${projectLat(25.0)}
    L ${projectLon(-80.1)} ${projectLat(26.5)}
    L ${projectLon(-80.0)} ${projectLat(29.0)}
    L ${projectLon(-81.5)} ${projectLat(30.7)}
    L ${projectLon(-80.8)} ${projectLat(32.0)}
    L ${projectLon(-79.0)} ${projectLat(33.0)}
    L ${projectLon(-78.5)} ${projectLat(33.9)}
    L ${projectLon(-75.5)} ${projectLat(35.2)}
    L ${projectLon(-75.8)} ${projectLat(36.5)}
    L ${projectLon(-76.0)} ${projectLat(37.0)}
    L ${projectLon(-75.2)} ${projectLat(38.0)}
    L ${projectLon(-74.0)} ${projectLat(38.9)}
    L ${projectLon(-74.0)} ${projectLat(40.5)}
    L ${projectLon(-73.9)} ${projectLat(40.8)}
    L ${projectLon(-71.0)} ${projectLat(41.5)}
    L ${projectLon(-70.0)} ${projectLat(41.5)}
    L ${projectLon(-70.0)} ${projectLat(43.5)}
    L ${projectLon(-67.0)} ${projectLat(44.5)}
    L ${projectLon(-67.0)} ${projectLat(47.5)}
    L ${projectLon(-83.0)} ${projectLat(46.0)}
    L ${projectLon(-84.5)} ${projectLat(46.5)}
    L ${projectLon(-88.0)} ${projectLat(48.0)}
    L ${projectLon(-89.5)} ${projectLat(48.0)}
    L ${projectLon(-95.0)} ${projectLat(49.0)}
    L ${projectLon(-123.0)} ${projectLat(49.0)}
    L ${projectLon(-124.7)} ${projectLat(48.4)}
    Z
  `;

  // Legislative history data
  const legislativeHistory = [
    { year: '2008', action: 'DHS declares eLoran as official GPS backup', status: 'Policy established but never funded' },
    { year: '2010', action: 'USCG terminates Loran-C operations', status: 'Infrastructure decommissioned; some sites retained' },
    { year: '2015', action: 'Howard Coble Coast Guard Authorization Act', status: 'Directed DOT to establish eLoran; unfunded' },
    { year: '2018', action: 'National Timing Resilience and Security Act (P.L. 115-282)', status: 'Mandated backup timing system; remains unfunded' },
    { year: '2020', action: 'Executive Order 13905 (PNT Resilience)', status: 'Critical infrastructure required to address PNT risks' },
    { year: '2021', action: 'NDAA Sec. 1609 (Loran Infrastructure)', status: 'Prohibited disposal of retained Loran sites' },
    { year: '2024', action: 'DHS S&T Loran-C Investigation Report', status: 'Recommended reconstitution; awaiting funding' }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a0f 0%, #0d1117 50%, #0a0f1a 100%)',
      color: '#e6edf3',
      fontFamily: '"DM Sans", system-ui, sans-serif',
      overflow: 'hidden'
    }}>
      {/* Animated Background Grid */}
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundImage: `
          linear-gradient(rgba(0, 212, 255, 0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0, 212, 255, 0.03) 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      {/* Floating Particles */}
      <div style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 1
      }}>
        {[...Array(20)].map((_, i) => (
          <div key={i} style={{
            position: 'absolute',
            width: '2px',
            height: '2px',
            background: i % 3 === 0 ? '#00d4ff' : i % 3 === 1 ? '#7c3aed' : '#10b981',
            borderRadius: '50%',
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 2}s`,
            opacity: 0.6
          }} />
        ))}
      </div>

      {/* Navigation */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: isMobile ? '0.75rem 1rem' : '1rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: scrollY > 50 || isMenuOpen ? 'rgba(10, 10, 15, 0.95)' : 'transparent',
        backdropFilter: scrollY > 50 || isMenuOpen ? 'blur(20px)' : 'none',
        borderBottom: scrollY > 50 || isMenuOpen ? '1px solid rgba(0, 212, 255, 0.1)' : 'none',
        zIndex: 1000,
        transition: 'all 0.3s ease'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            width: isMobile ? '36px' : '44px',
            height: isMobile ? '36px' : '44px',
            background: 'linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '800',
            fontSize: isMobile ? '0.9rem' : '1.1rem',
            color: '#0a0a0f',
            letterSpacing: '-1px'
          }}>VT</div>
          <div>
            <div style={{ fontWeight: '700', fontSize: isMobile ? '1rem' : '1.25rem', letterSpacing: '-0.5px' }}>VisuTel</div>
            <div style={{ fontSize: '0.6rem', color: '#00d4ff', letterSpacing: '1.5px', textTransform: 'uppercase' }}>v2.85</div>
          </div>
        </div>
        
        {/* Mobile Hamburger Menu */}
        {isMobile && (
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '5px',
              zIndex: 1001
            }}
          >
            <span style={{
              width: '24px',
              height: '2px',
              background: '#00d4ff',
              transition: 'all 0.3s',
              transform: isMenuOpen ? 'rotate(45deg) translateY(7px)' : 'none'
            }} />
            <span style={{
              width: '24px',
              height: '2px',
              background: '#00d4ff',
              transition: 'all 0.3s',
              opacity: isMenuOpen ? 0 : 1
            }} />
            <span style={{
              width: '24px',
              height: '2px',
              background: '#00d4ff',
              transition: 'all 0.3s',
              transform: isMenuOpen ? 'rotate(-45deg) translateY(-7px)' : 'none'
            }} />
          </button>
        )}

        {/* Desktop Nav Links */}
        {!isMobile && (
        <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
          {/* Home link */}
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{
              color: '#8b949e',
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'color 0.2s',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0
            }} 
            onMouseOver={e => e.target.style.color = '#00d4ff'} 
            onMouseOut={e => e.target.style.color = '#8b949e'}
          >
            Home
          </button>

          {/* Technology link */}
          <button 
            onClick={() => scrollToSection('technology')}
            style={{
              color: '#8b949e',
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'color 0.2s',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0
            }} 
            onMouseOver={e => e.target.style.color = '#00d4ff'} 
            onMouseOut={e => e.target.style.color = '#8b949e'}
          >
            Technology
          </button>
          
          {/* Legislative History with dropdown */}
          <div style={{ position: 'relative' }}>
            <button 
              onClick={() => scrollToSection('legislative')}
              style={{
                color: '#8b949e',
                textDecoration: 'none',
                fontSize: '0.875rem',
                fontWeight: '500',
                transition: 'color 0.2s',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }} 
              onMouseOver={e => e.currentTarget.style.color = '#00d4ff'} 
              onMouseOut={e => e.currentTarget.style.color = '#8b949e'}
            >
              Legislative History
              <span style={{ fontSize: '0.6rem' }}>-</span>
            </button>
            {/* Dropdown trigger area */}
            <div 
              style={{
                position: 'absolute',
                top: '100%',
                left: '-10px',
                paddingTop: '8px',
                opacity: 0,
                visibility: 'hidden',
                transition: 'opacity 0.2s, visibility 0.2s'
              }}
              className="leg-dropdown"
            >
              <div style={{
                background: 'rgba(10, 10, 15, 0.98)',
                border: '1px solid rgba(124, 58, 237, 0.3)',
                borderRadius: '8px',
                padding: '8px 0',
                minWidth: '200px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
              }}>
                <button
                  onClick={() => {
                    scrollToSection('legislative');
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 16px',
                    background: 'none',
                    border: 'none',
                    color: '#8b949e',
                    fontSize: '0.8rem',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'background 0.2s, color 0.2s'
                  }}
                  onMouseOver={e => {
                    e.target.style.background = 'rgba(0, 212, 255, 0.1)';
                    e.target.style.color = '#00d4ff';
                  }}
                  onMouseOut={e => {
                    e.target.style.background = 'none';
                    e.target.style.color = '#8b949e';
                  }}
                >
                  Overview (2008-2024)
                </button>
                <button
                  onClick={() => {
                    setShow2025Actions(true);
                    scrollToSection('exec-actions-2025');
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 16px',
                    background: 'none',
                    border: 'none',
                    color: '#7c3aed',
                    fontSize: '0.8rem',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    fontWeight: '600'
                  }}
                  onMouseOver={e => {
                    e.target.style.background = 'rgba(124, 58, 237, 0.15)';
                  }}
                  onMouseOut={e => {
                    e.target.style.background = 'none';
                  }}
                >
                  2025 Executive Actions *
                </button>
              </div>
            </div>
          </div>
          
          {/* Other nav links */}
          {[
            { label: 'eLoran', id: 'eloran' },
            { label: 'QTPP Dashboard', id: 'dashboard' },
            { label: 'Contact', id: 'contact' }
          ].map((item, i) => (
            <button 
              key={item.id} 
              onClick={() => scrollToSection(item.id)}
              style={{
                color: '#8b949e',
                textDecoration: 'none',
                fontSize: '0.875rem',
                fontWeight: '500',
                transition: 'color 0.2s',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                padding: 0
              }} 
              onMouseOver={e => e.target.style.color = '#00d4ff'} 
              onMouseOut={e => e.target.style.color = '#8b949e'}
            >
              {item.label}
            </button>
          ))}
          <button 
            onClick={() => scrollToSection('contact')}
            style={{
              background: 'linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)',
              border: 'none',
              padding: '0.625rem 1.5rem',
              borderRadius: '8px',
              color: '#0a0a0f',
              fontWeight: '600',
              fontSize: '0.875rem',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }} 
            onMouseOver={e => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 25px rgba(0, 212, 255, 0.3)';
            }} 
            onMouseOut={e => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            Pre-Register
          </button>
        </div>
        )}

        {/* Mobile Nav Menu - Right aligned dropdown */}
        {isMobile && isMenuOpen && (
          <div style={{
            position: 'fixed',
            top: '60px',
            right: 0,
            width: '75%',
            maxWidth: '280px',
            bottom: 0,
            background: 'rgba(10, 10, 15, 0.98)',
            backdropFilter: 'blur(20px)',
            display: 'flex',
            flexDirection: 'column',
            padding: '2rem',
            gap: '1rem',
            zIndex: 999,
            borderLeft: '1px solid rgba(0, 212, 255, 0.2)'
          }}>
            {[
              { label: 'Home', id: '_top' },
              { label: 'Technology', id: 'technology' },
              { label: 'Legislative History', id: 'legislative' },
              { label: 'eLoran', id: 'eloran' },
              { label: 'QTPP Dashboard', id: 'dashboard' },
              { label: 'Contact', id: 'contact' }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  if (item.id === '_top') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } else {
                    scrollToSection(item.id);
                  }
                  setIsMenuOpen(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#e6edf3',
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  padding: '1rem',
                  textAlign: 'left',
                  borderBottom: '1px solid rgba(0, 212, 255, 0.1)',
                  cursor: 'pointer'
                }}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => {
                scrollToSection('contact');
                setIsMenuOpen(false);
              }}
              style={{
                background: 'linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)',
                border: 'none',
                padding: '1rem 2rem',
                borderRadius: '10px',
                color: '#0a0a0f',
                fontWeight: '700',
                fontSize: '1rem',
                cursor: 'pointer',
                marginTop: '1rem'
              }}
            >
              Pre-Register
            </button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        padding: isMobile ? '6rem 1.5rem 2rem' : isTablet ? '7rem 2rem 3rem' : '8rem 4rem 4rem',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
          <div style={{
            display: 'inline-block',
            padding: '0.5rem 1rem',
            background: 'rgba(0, 212, 255, 0.1)',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            borderRadius: '50px',
            marginBottom: '1.5rem',
            fontSize: isMobile ? '0.65rem' : '0.8rem',
            color: '#00d4ff',
            letterSpacing: '1px'
          }}>{'\u2726'} GNSS-INDEPENDENT TIMING & POSITIONING
          </div>
          
          <h1 style={{
            fontSize: isMobile ? 'clamp(2rem, 10vw, 2.5rem)' : 'clamp(3rem, 8vw, 5.5rem)',
            fontWeight: '800',
            lineHeight: '1.05',
            marginBottom: '1.5rem',
            letterSpacing: '-2px'
          }}>
            <span style={{ color: '#e6edf3' }}>Physics-Gated Trust</span>
            <br />
            <span style={{
              background: 'linear-gradient(135deg, #00d4ff 0%, #7c3aed 50%, #10b981 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>for Critical Infrastructure</span>
          </h1>
          
          <p style={{
            fontSize: isMobile ? '1rem' : '1.35rem',
            color: '#8b949e',
            maxWidth: '650px',
            lineHeight: '1.7',
            marginBottom: isMobile ? '1.5rem' : '2.5rem'
          }}>
            The <strong style={{ color: '#e6edf3' }}>Quantum Timing and Positioning Protocol (QTPP)</strong> delivers 
            sub-nanosecond timing accuracy and quantum-secured communications-eliminating vulnerabilities 
            to jamming, spoofing, and single-point-of-failure dependencies.
          </p>
          
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '1rem', marginBottom: isMobile ? '2rem' : '4rem' }}>
            <button 
              onClick={() => scrollToSection('technology')}
              style={{
                background: 'linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)',
                border: 'none',
                padding: isMobile ? '0.875rem 1.5rem' : '1rem 2rem',
                borderRadius: '10px',
                color: '#0a0a0f',
                fontWeight: '700',
                fontSize: isMobile ? '0.9rem' : '1rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s'
              }}
            >
              Explore Technology
              <span style={{ fontSize: '1.2rem' }}>{'\u2192'}</span>
            </button>
            <button 
              onClick={() => scrollToSection('legislative')}
              style={{
                background: 'transparent',
                border: '2px solid rgba(0, 212, 255, 0.5)',
                padding: isMobile ? '0.875rem 1.5rem' : '1rem 2rem',
                borderRadius: '10px',
                color: '#00d4ff',
                fontWeight: '600',
                fontSize: isMobile ? '0.9rem' : '1rem',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
            >
              View Legislative History
            </button>
          </div>

          {/* Stats Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: isMobile ? '1rem' : '2rem',
            padding: isMobile ? '1rem' : '2rem',
            background: 'rgba(0, 212, 255, 0.03)',
            border: '1px solid rgba(0, 212, 255, 0.1)',
            borderRadius: '16px'
          }}>
            {[
              { value: `${patentCount}+`, suffix: '', subtext: 'patent pending of portfolio of 80', subtext2: 'Identified patent opportunities', label: '', color: '#00d4ff' },
              { value: claimCount, suffix: '+', subtext: '', subtext2: '', label: 'Total Claims', color: '#7c3aed' },
              { value: '80-145', suffix: 'B', subtext: '', subtext2: '', label: 'Portfolio Value', color: '#10b981' },
              { value: `${coveragePercent}%`, suffix: '', subtext: '', subtext2: '', label: 'CONUS Coverage', color: '#f59e0b' }
            ].map((stat, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '2.75rem',
                  fontWeight: '800',
                  color: stat.color,
                  fontSize: isMobile ? '1.75rem' : '2.75rem',
                  fontWeight: '800',
                  color: stat.color,
                  letterSpacing: '-2px'
                }}>
                  {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                  <span style={{ fontSize: isMobile ? '1rem' : '1.5rem' }}>{stat.suffix}</span>
                </div>
                {stat.subtext && (
                  <div style={{ fontSize: isMobile ? '0.65rem' : '0.85rem', color: '#8b949e', marginTop: '-0.25rem' }}>{stat.subtext}</div>
                )}
                {stat.subtext2 && (
                  <div style={{ fontSize: isMobile ? '0.65rem' : '0.85rem', color: '#8b949e', marginTop: '0rem' }}>{stat.subtext2}</div>
                )}
                {stat.label && (
                  <div style={{ color: '#8b949e', fontSize: isMobile ? '0.75rem' : '0.9rem', marginTop: '0.25rem' }}>{stat.label}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 01: Technology / Legacy IP */}
      <section id="technology" style={{
        padding: isMobile ? '3rem 1.5rem' : '6rem 4rem',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: isMobile ? '2rem' : '3rem' }}>
            <span style={{
              fontSize: isMobile ? '3rem' : '5rem',
              fontWeight: '800',
              color: 'rgba(0, 212, 255, 0.15)',
              lineHeight: '1',
              letterSpacing: '-4px'
            }}>01</span>
            <div>
              <div style={{ color: '#00d4ff', fontSize: isMobile ? '0.65rem' : '0.8rem', letterSpacing: '3px', marginBottom: '0.5rem' }}>TECHNOLOGY</div>
              <h2 style={{ fontSize: isMobile ? '1.5rem' : '2.5rem', fontWeight: '700', letterSpacing: '-1px' }}>The Legacy That Started It All</h2>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1.2fr',
            gap: isMobile ? '2rem' : '4rem',
            alignItems: 'center',
            marginBottom: isMobile ? '2rem' : '4rem'
          }}>
            <div>
              <div style={{
                background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.1) 0%, rgba(0, 212, 255, 0.05) 100%)',
                border: '1px solid rgba(124, 58, 237, 0.3)',
                borderRadius: '20px',
                padding: isMobile ? '1.5rem' : '2.5rem',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-20px',
                  right: '-20px',
                  width: '100px',
                  height: '100px',
                  background: 'radial-gradient(circle, rgba(124, 58, 237, 0.3) 0%, transparent 70%)',
                  borderRadius: '50%'
                }} />
                
                <div style={{
                  fontSize: '0.75rem',
                  color: '#7c3aed',
                  letterSpacing: '2px',
                  marginBottom: '1rem'
                }}>U.S. PATENT</div>
                
                <h3 style={{
                  fontSize: '2.5rem',
                  fontWeight: '800',
                  color: '#e6edf3',
                  marginBottom: '0.5rem',
                  letterSpacing: '-1px'
                }}>#7,136,376</h3>
                
                <div style={{
                  fontSize: '1.1rem',
                  color: '#8b949e',
                  marginBottom: '1.5rem'
                }}>Filed 2002  Granted 2006</div>
                
                <h4 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#e6edf3',
                  marginBottom: '1rem',
                  lineHeight: '1.4'
                }}>
                  Public Switched Broadband Network (PSBN)
                </h4>
                
                <p style={{
                  color: '#8b949e',
                  lineHeight: '1.7',
                  fontSize: '0.95rem',
                  marginBottom: '1.5rem'
                }}>
                  The original "last mile" solution that revolutionized telecommunications infrastructure through 
                  systematic architectural thinking — the same methodology now applied to timing and positioning challenges.
                </p>

                <div style={{
                  padding: '1rem',
                  background: 'rgba(124, 58, 237, 0.1)',
                  borderRadius: '10px',
                  borderLeft: '3px solid #7c3aed'
                }}>
                  <div style={{ fontSize: '0.8rem', color: '#7c3aed', marginBottom: '0.25rem' }}>INVENTOR</div>
                  <div style={{ color: '#e6edf3', fontWeight: '600' }}>
                    In 2002, Avraham Ben Levi Yisra'EL
                  </div>
                  <div style={{ color: '#8b949e', fontSize: '0.9rem' }}>
                    CEO and FOUNDER of VisuTel
                  </div>
                </div>

                {/* Industry Infringement & Settlement Strategy */}
                <div style={{
                  marginTop: '1.5rem',
                  padding: '1.25rem',
                  background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(245, 158, 11, 0.08) 100%)',
                  borderRadius: '12px',
                  border: '1px solid rgba(239, 68, 68, 0.25)'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#ef4444', letterSpacing: '2px', marginBottom: '0.75rem', fontWeight: '700' }}>PATENT #7,136,376 — INFRINGEMENT & SETTLEMENT (2020–2024)</div>
                  
                  <p style={{
                    color: '#8b949e',
                    lineHeight: '1.7',
                    fontSize: '0.9rem',
                    marginBottom: '1rem'
                  }}>
                    U.S. Patent #7,136,376 — the <strong style={{ color: '#e6edf3' }}>Public Switched Broadband Network (PSBN)</strong> — covers 
                    foundational end-to-end broadband delivery architecture now embedded across the telecommunications industry. 
                    From 2020 to 2024, major carriers and ISPs have deployed last-mile infrastructure, flat-architecture broadband 
                    delivery systems, and end-to-end switching methodologies that read directly on the claims of this granted patent. 
                    Settlement and licensing proceeds from enforcement will <strong style={{ color: '#10b981' }}>directly fund the development and 
                    deployment of QTPP infrastructure</strong>.
                  </p>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: '0.75rem',
                    marginBottom: '1rem'
                  }}>
                    <div style={{
                      padding: '0.75rem',
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '8px',
                      borderLeft: '3px solid #ef4444'
                    }}>
                      <div style={{ fontSize: '0.7rem', color: '#ef4444', fontWeight: '600', marginBottom: '0.25rem' }}>ESTIMATED INFRINGEMENT DAMAGES (2020–2024)</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#ef4444', marginBottom: '0.25rem' }}>$500M–2B+</div>
                      <div style={{ fontSize: '0.75rem', color: '#8b949e', lineHeight: '1.5' }}>
                        Based on industry royalty rates for broadband delivery architecture across major U.S. carriers and ISPs during the 2020–2024 infringement period
                      </div>
                    </div>
                    <div style={{
                      padding: '0.75rem',
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '8px',
                      borderLeft: '3px solid #f59e0b'
                    }}>
                      <div style={{ fontSize: '0.7rem', color: '#f59e0b', fontWeight: '600', marginBottom: '0.25rem' }}>INFRINGING PARTIES (2020–2024)</div>
                      <div style={{ fontSize: '0.8rem', color: '#e6edf3', lineHeight: '1.6' }}>
                        Major U.S. Carriers (AT&T, Verizon, T-Mobile) &bull; Cable/ISP Providers (Comcast, Charter, Cox) &bull; Fiber Overbuilders &bull; Fixed Wireless Access Deployers &bull; Last-Mile Infrastructure Operators
                      </div>
                    </div>
                  </div>

                  <div style={{
                    padding: '1rem',
                    background: 'rgba(16, 185, 129, 0.08)',
                    borderRadius: '10px',
                    border: '1px solid rgba(16, 185, 129, 0.25)'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: '#10b981', letterSpacing: '1.5px', marginBottom: '0.5rem', fontWeight: '700' }}>SETTLEMENT STRATEGY — FUNDING QTPP</div>
                    <p style={{
                      color: '#8b949e',
                      lineHeight: '1.7',
                      fontSize: '0.85rem',
                      marginBottom: '0.75rem'
                    }}>
                      VisuTel pursues a <strong style={{ color: '#e6edf3' }}>dual-path settlement strategy</strong> for Patent #7,136,376 
                      enforcement — channeling all proceeds into QTPP development and national timing infrastructure deployment:
                    </p>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                      gap: '0.75rem'
                    }}>
                      <div style={{
                        padding: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.25)',
                        borderRadius: '8px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <span style={{ fontSize: '1.1rem' }}>💰</span>
                          <span style={{ fontWeight: '700', color: '#10b981', fontSize: '0.85rem' }}>Cash Settlements</span>
                        </div>
                        <p style={{ fontSize: '0.8rem', color: '#8b949e', lineHeight: '1.5', margin: 0 }}>
                          Royalty and lump-sum licensing for past infringement (2020–2024) of Patent #7,136,376's broadband 
                          delivery architecture claims. Proceeds fund QTPP patent prosecution, pilot infrastructure, 
                          and eLoran demonstration programs.
                        </p>
                      </div>
                      <div style={{
                        padding: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.25)',
                        borderRadius: '8px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <span style={{ fontSize: '1.1rem' }}>🏗️</span>
                          <span style={{ fontWeight: '700', color: '#10b981', fontSize: '0.85rem' }}>Bartered Infrastructure</span>
                        </div>
                        <p style={{ fontSize: '0.8rem', color: '#8b949e', lineHeight: '1.5', margin: 0 }}>
                          Infringing carriers may offset Patent #7,136,376 obligations through in-kind contributions: 
                          tower access, fiber leases, spectrum rights, equipment provisioning, and co-deployment of 
                          QTPP-enhanced eLoran stations — converting broadband patent liability into national timing 
                          infrastructure.
                        </p>
                      </div>
                    </div>
                    <p style={{
                      color: '#8b949e',
                      lineHeight: '1.6',
                      fontSize: '0.8rem',
                      marginTop: '0.75rem',
                      marginBottom: 0,
                      fontStyle: 'italic'
                    }}>
                      This approach transforms Patent #7,136,376 enforcement into a funding engine for QTPP — leveraging 
                      two decades of broadband infrastructure infringement to capitalize the next generation of quantum timing 
                      and positioning infrastructure, fulfilling the unfunded National Timing Resilience and Security Act mandate.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 style={{
                fontSize: '1.75rem',
                fontWeight: '700',
                marginBottom: '1.5rem',
                color: '#e6edf3'
              }}>From Broadband to Quantum Infrastructure</h3>
              
              <p style={{
                color: '#8b949e',
                lineHeight: '1.8',
                marginBottom: '1.5rem',
                fontSize: '1.05rem'
              }}>
                In 2002, VisuTel identified and solved the telecommunications "last mile" problem — the final 
                connection between network infrastructure and end users. Two decades later, we've applied 
                that same systematic thinking to a far more critical challenge: <strong style={{ color: '#e6edf3' }}>
                the "ground truth" problem in modern infrastructure.</strong>
              </p>
            </div>
          </div>

          {/* Core Quartet - Moved Here */}
          <div style={{
            marginBottom: '3rem',
            padding: isMobile ? '1rem' : '2rem',
            background: 'rgba(16, 185, 129, 0.05)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: '16px',
            overflow: 'hidden'
          }}>
            <h3 style={{
              fontSize: isMobile ? '1rem' : '1.25rem',
              fontWeight: '600',
              color: '#10b981',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{
                width: '8px',
                height: '8px',
                background: '#10b981',
                borderRadius: '50%',
                boxShadow: '0 0 10px #10b981',
                flexShrink: 0
              }} />
              The Core Quartet - Foundational Architecture
            </h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
              gap: isMobile ? '1rem' : '1.5rem'
            }}>
              {[
                { id: 'P1', name: 'Phase-SSR', desc: 'Timing foundation layer', layer: 'L0' },
                { id: 'P6', name: 'TPO', desc: 'Time Position Objects via 15D-QKF/LSTM', layer: 'L1' },
                { id: 'P12', name: 'Quantum Positioning Core', desc: 'Heisenberg-limited triangulation', layer: 'L1' },
                { id: 'P22', name: 'PSSR', desc: 'Phase Synchronized Secure Routing', layer: 'L3' }
              ].map((patent, i) => (
                <div key={i} style={{
                  padding: isMobile ? '1rem' : '1.5rem',
                  background: 'rgba(10, 10, 15, 0.5)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: '12px',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    padding: '0.25rem 0.5rem',
                    background: 'rgba(16, 185, 129, 0.2)',
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    color: '#10b981',
                    fontWeight: '600'
                  }}>{patent.layer}</div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#8b949e',
                    marginBottom: '0.5rem'
                  }}>{patent.id}</div>
                  <div style={{
                    fontSize: isMobile ? '1rem' : '1.1rem',
                    fontWeight: '700',
                    color: '#e6edf3',
                    marginBottom: '0.5rem'
                  }}>{patent.name}</div>
                  <div style={{
                    fontSize: '0.85rem',
                    color: '#8b949e',
                    lineHeight: '1.5'
                  }}>{patent.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 5-Layer Architecture - Moved Here */}
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            marginBottom: '1.5rem',
            color: '#e6edf3'
          }}>QTPP 5-Layer Architecture</h3>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            marginBottom: '3rem'
          }}>
            {[
              { layer: 'L4', name: 'Applications', desc: 'Domain-specific timing services', color: '#f59e0b', patents: 'P30-P45' },
              { layer: 'L3', name: 'PSSR Distribution', desc: 'Phase Synchronized Secure Routing', color: '#ef4444', patents: 'P22-P29' },
              { layer: 'L2', name: 'Security', desc: 'P13 Canonical SDN, quantum authentication', color: '#8b5cf6', patents: 'P13-P21' },
              { layer: 'L1', name: 'Intelligence', desc: 'TPO creation via 15D-QKF/LSTM, TAN nodes', color: '#00d4ff', patents: 'P6-P12' },
              { layer: 'L0', name: 'Phase-SSR Foundation', desc: 'Optical atomic clock timing substrate', color: '#10b981', patents: 'P1-P5' }
            ].map((layer, i) => (
              <div key={i} style={{
                display: isMobile ? 'flex' : 'grid',
                flexDirection: isMobile ? 'column' : undefined,
                gridTemplateColumns: isMobile ? undefined : '80px 180px 1fr 120px',
                alignItems: isMobile ? 'flex-start' : 'center',
                gap: isMobile ? '0.25rem' : '1.5rem',
                padding: isMobile ? '1rem' : '1.25rem 1.5rem',
                background: `linear-gradient(90deg, ${layer.color}15 0%, transparent 100%)`,
                borderLeft: `4px solid ${layer.color}`,
                borderRadius: '0 12px 12px 0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  width: isMobile ? '100%' : undefined
                }}>
                  <div style={{
                    fontWeight: '800',
                    fontSize: isMobile ? '1.1rem' : '1.25rem',
                    color: layer.color,
                    minWidth: isMobile ? '32px' : undefined
                  }}>{layer.layer}</div>
                  {isMobile && (
                    <div style={{
                      fontWeight: '600',
                      color: '#e6edf3',
                      fontSize: '0.95rem'
                    }}>{layer.name}</div>
                  )}
                  {isMobile && (
                    <div style={{
                      fontSize: '0.7rem',
                      color: '#8b949e',
                      marginLeft: 'auto'
                    }}>{layer.patents}</div>
                  )}
                </div>
                {!isMobile && (
                  <div style={{
                    fontWeight: '600',
                    color: '#e6edf3'
                  }}>{layer.name}</div>
                )}
                <div style={{
                  color: '#8b949e',
                  fontSize: isMobile ? '0.8rem' : '0.9rem',
                  paddingLeft: isMobile ? '2.5rem' : undefined
                }}>{layer.desc}</div>
                {!isMobile && (
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#8b949e',
                    textAlign: 'right'
                  }}>{layer.patents}</div>
                )}
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Section 02: U.S. Legislative History */}
      <section id="legislative" style={{
        padding: isMobile ? '3rem 1.5rem' : '6rem 4rem',
        background: 'linear-gradient(180deg, transparent 0%, rgba(0, 212, 255, 0.02) 50%, transparent 100%)',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '3rem' }}>
            <span style={{
              fontSize: isMobile ? '3rem' : '5rem',
              fontWeight: '800',
              color: 'rgba(0, 212, 255, 0.15)',
              lineHeight: '1',
              letterSpacing: '-4px'
            }}>02</span>
            <div>
              <div style={{ color: '#00d4ff', fontSize: '0.8rem', letterSpacing: '3px', marginBottom: '0.5rem' }}>U.S. LEGISLATIVE HISTORY</div>
              <h2 style={{ fontSize: isMobile ? '1.5rem' : '2.5rem', fontWeight: '700', letterSpacing: '-1px' }}>Two Decades of Unfunded Mandates</h2>
            </div>
          </div>

          <p style={{
            color: '#8b949e',
            fontSize: '1.15rem',
            maxWidth: '900px',
            marginBottom: '3rem',
            lineHeight: '1.7'
          }}>
            The United States has recognized GNSS vulnerability as a <strong style={{ color: '#e6edf3' }}>national security issue</strong> for 
            over two decades, resulting in multiple legislative actions that remain incompletely implemented.
          </p>

          {/* Legislative Timeline Table */}
          <div style={{
            background: 'rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            borderRadius: '16px',
            overflow: 'hidden',
            marginBottom: '3rem'
          }}>
            {/* Table Header */}
            <div style={{
              display: isMobile ? 'none' : 'grid',
              gridTemplateColumns: '100px 1fr 1fr',
              gap: '1rem',
              padding: '1rem 1.5rem',
              background: 'rgba(0, 212, 255, 0.1)',
              borderBottom: '1px solid rgba(0, 212, 255, 0.2)'
            }}>
              <div style={{ fontWeight: '700', color: '#00d4ff', fontSize: '0.85rem' }}>YEAR</div>
              <div style={{ fontWeight: '700', color: '#00d4ff', fontSize: '0.85rem' }}>ACTION</div>
              <div style={{ fontWeight: '700', color: '#00d4ff', fontSize: '0.85rem' }}>STATUS</div>
            </div>
            
            {/* Table Rows */}
            {legislativeHistory.map((item, i) => (
              <div key={i} style={{
                display: isMobile ? 'flex' : 'grid',
                flexDirection: isMobile ? 'column' : undefined,
                gridTemplateColumns: isMobile ? undefined : '100px 1fr 1fr',
                gap: isMobile ? '0.5rem' : '1rem',
                padding: isMobile ? '1rem' : '1.25rem 1.5rem',
                borderBottom: i < legislativeHistory.length - 1 ? '1px solid rgba(0, 212, 255, 0.1)' : 'none',
                transition: 'background 0.2s'
              }} onMouseOver={e => e.currentTarget.style.background = 'rgba(0, 212, 255, 0.05)'} 
                 onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{
                  fontWeight: '700',
                  color: '#f59e0b',
                  fontSize: isMobile ? '1rem' : '1.1rem'
                }}>{item.year}</div>
                <div style={{
                  color: '#e6edf3',
                  fontSize: isMobile ? '0.85rem' : '0.95rem',
                  lineHeight: '1.5'
                }}>{item.action}</div>
                <div style={{
                  color: '#8b949e',
                  fontSize: isMobile ? '0.8rem' : '0.9rem',
                  lineHeight: '1.5',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    marginTop: '6px',
                    background: item.status.includes('unfunded') || item.status.includes('never funded') || item.status.includes('awaiting') || item.status.includes('remains') 
                      ? '#ef4444' 
                      : item.status.includes('decommissioned') || item.status.includes('Prohibited')
                      ? '#f59e0b'
                      : '#10b981',
                    flexShrink: 0
                  }} />
                  {item.status}
                </div>
              </div>
            ))}
          </div>

          {/* 2025 Executive Actions */}
          <div id="exec-actions-2025" style={{
            marginTop: '3rem',
            marginBottom: '3rem'
          }}>
            <h3 
              onClick={() => setShow2025Actions(!show2025Actions)}
              style={{
                color: '#7c3aed',
                fontSize: '1.25rem',
                fontWeight: '700',
                marginBottom: show2025Actions ? '1.5rem' : '0',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                cursor: 'pointer',
                transition: 'color 0.2s'
              }}
              onMouseOver={e => e.currentTarget.style.color = '#a78bfa'}
              onMouseOut={e => e.currentTarget.style.color = '#7c3aed'}
            >
              <span style={{
                width: '10px',
                height: '10px',
                background: '#7c3aed',
                borderRadius: '50%',
                boxShadow: '0 0 10px #7c3aed'
              }} />
              2025 Executive Actions — PNT & Critical Infrastructure Timing
              <span style={{
                marginLeft: 'auto',
                fontSize: '0.8rem',
                transform: show2025Actions ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s'
              }}>-</span>
            </h3>
            
            {show2025Actions && (
            <div style={{
              background: 'rgba(124, 58, 237, 0.05)',
              border: '1px solid rgba(124, 58, 237, 0.2)',
              borderRadius: '16px',
              overflow: 'hidden'
            }}>
              {/* Table Header - hidden on mobile */}
              <div style={{
                display: isMobile ? 'none' : 'grid',
                gridTemplateColumns: '120px 1fr 1.5fr',
                gap: '1rem',
                padding: '1rem 1.5rem',
                background: 'rgba(124, 58, 237, 0.15)',
                borderBottom: '1px solid rgba(124, 58, 237, 0.2)'
              }}>
                <div style={{ fontWeight: '700', color: '#7c3aed', fontSize: '0.85rem' }}>DATE</div>
                <div style={{ fontWeight: '700', color: '#7c3aed', fontSize: '0.85rem' }}>INSTRUMENT</div>
                <div style={{ fontWeight: '700', color: '#7c3aed', fontSize: '0.85rem' }}>PNT / TIMING RELEVANCE</div>
              </div>
              
              {/* Table Rows - 2025 Only */}
              {[
                { date: 'Jan 23, 2025', instrument: 'EO 14179 — Removing Barriers to American Leadership in AI', relevance: 'Directs agencies to use AI to strengthen resilience of PNT-dependent critical infrastructure; national-security carve-out enables tailored multi-PNT and timing-AI architectures.' },
                { date: 'Jul 23, 2025', instrument: 'EO 14318 — Accelerating Federal Permitting of Data Center Infrastructure', relevance: 'Streamlines permitting for AI data centers and grid upgrades that host AI-enabled timing-resilience and PNT-monitoring systems.' },
                { date: 'Jul 31, 2025', instrument: "America's AI Action Plan", relevance: 'Prioritizes AI-driven clock-network sync, PNT-sensor fusion, and real-time timing-network monitoring; DOE/NIST roles in resilient timing and multi-PNT R&D.' },
                { date: 'Nov 2025', instrument: '"Genesis Mission" EO', relevance: 'Directs DOE to use AI for PNT-adjacent R&D (quantum clocks, timing-distribution, multi-sensor fusion) and GNSS-denied modeling.' },
                { date: 'Dec 11, 2025', instrument: 'EO — Ensuring a National Policy Framework for AI', relevance: 'DOJ AI Litigation Task Force and Commerce evaluation of state AI laws; preemption and funding-leverage mechanisms that affect AI-enabled critical-infrastructure-timing deployments.' }
              ].map((item, i) => (
                <div key={i} style={{
                  display: isMobile ? 'flex' : 'grid',
                  flexDirection: isMobile ? 'column' : undefined,
                  gridTemplateColumns: isMobile ? undefined : '120px 1fr 1.5fr',
                  gap: isMobile ? '0.5rem' : '1rem',
                  padding: isMobile ? '1rem' : '1rem 1.5rem',
                  borderBottom: i < 4 ? '1px solid rgba(124, 58, 237, 0.1)' : 'none',
                  transition: 'background 0.2s'
                }} onMouseOver={e => e.currentTarget.style.background = 'rgba(124, 58, 237, 0.08)'} 
                   onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{
                    fontWeight: '600',
                    color: '#7c3aed',
                    fontSize: isMobile ? '0.85rem' : '0.9rem'
                  }}>{item.date}</div>
                  <div style={{
                    color: '#e6edf3',
                    fontSize: isMobile ? '0.85rem' : '0.9rem',
                    fontWeight: '500',
                    lineHeight: '1.4'
                  }}>{item.instrument}</div>
                  <div style={{
                    color: '#8b949e',
                    fontSize: isMobile ? '0.8rem' : '0.85rem',
                    lineHeight: '1.5'
                  }}>{item.relevance}</div>
                </div>
              ))}
            </div>
            )}
          </div>

          {/* Funding Gap Section */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '2rem'
          }}>
            <div style={{
              padding: '2rem',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '16px'
            }}>
              <h3 style={{
                color: '#ef4444',
                fontSize: '1.25rem',
                fontWeight: '700',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <span style={{ fontSize: '1.5rem' }}>{'\u0024'}</span>
                The Funding Gap
              </h3>
              
              <p style={{
                color: '#8b949e',
                lineHeight: '1.7',
                marginBottom: '1.5rem'
              }}>
                Despite clear legislative direction, <strong style={{ color: '#e6edf3' }}>no appropriation has been made</strong> to 
                reconstitute eLoran. The estimated cost:
              </p>

              <div style={{
                display: 'flex',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  flex: 1,
                  padding: '1rem',
                  background: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '10px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#ef4444' }}>$200-400M</div>
                  <div style={{ fontSize: '0.8rem', color: '#8b949e' }}>Infrastructure</div>
                </div>
                <div style={{
                  flex: 1,
                  padding: '1rem',
                  background: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '10px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#f59e0b' }}>$20-40M</div>
                  <div style={{ fontSize: '0.8rem', color: '#8b949e' }}>Annual Operations</div>
                </div>
              </div>

              <div style={{ color: '#8b949e', fontSize: '0.9rem', lineHeight: '1.6' }}>
                <strong style={{ color: '#e6edf3' }}>Congressional appropriators have consistently declined to fund, citing:</strong>
                <ul style={{ marginTop: '0.75rem', paddingLeft: '1.25rem' }}>
                  <li>Budget competition with other USCG priorities</li>
                  <li>Uncertainty about technology approach</li>
                  <li>Lack of visible industry partner with credible plan</li>
                  <li>Interagency disputes (DOT vs. DHS vs. DOD)</li>
                </ul>
              </div>
            </div>

            <div style={{
              padding: '2rem',
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '16px'
            }}>
              <h3 style={{
                color: '#10b981',
                fontSize: '1.25rem',
                fontWeight: '700',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <span style={{ fontSize: '1.5rem' }}>{'\u25CE'}</span>
                THE OPPORTUNITY
              </h3>
              
              <p style={{
                color: '#e6edf3',
                lineHeight: '1.8',
                fontSize: '1.1rem',
                marginBottom: '1.5rem'
              }}>
                VisuTel can <strong style={{ color: '#10b981' }}>break the logjam</strong> by offering a public-private 
                partnership that:
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {[
                  { icon: '\u2713', text: 'Reduces government cost through private investment' },
                  { icon: '\u2713', text: 'Accelerates deployment with proven technology' },
                  { icon: '\u2713', text: 'Demonstrates viability through limited pilot' },
                  { icon: '\u2713', text: 'Eliminates need for full $200-400M upfront' }
                ].map((item, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem 1rem',
                    background: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: '8px'
                  }}>
                    <span style={{ color: '#10b981', fontWeight: '700' }}>{item.icon}</span>
                    <span style={{ color: '#e6edf3' }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 03: eLoran */}
      <section id="eloran" style={{
        padding: isMobile ? '3rem 1.5rem' : '6rem 4rem',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '3rem' }}>
            <span style={{
              fontSize: isMobile ? '3rem' : '5rem',
              fontWeight: '800',
              color: 'rgba(0, 212, 255, 0.15)',
              lineHeight: '1',
              letterSpacing: '-4px'
            }}>03</span>
            <div>
              <div style={{ color: '#00d4ff', fontSize: '0.8rem', letterSpacing: '3px', marginBottom: '0.5rem' }}>ELORAN ENHANCEMENT</div>
              <h2 style={{ fontSize: isMobile ? '1.5rem' : '2.5rem', fontWeight: '700', letterSpacing: '-1px' }}>Quantum-Safe eLoran Infrastructure</h2>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '4rem'
          }}>
            <div>
              <p style={{
                color: '#8b949e',
                lineHeight: '1.8',
                marginBottom: '2rem',
                fontSize: '1.1rem'
              }}>
                The <strong style={{ color: '#e6edf3' }}>National Timing Resilience and Security Act</strong> mandates 
                GPS backup infrastructurebut funding remains incomplete. VisuTel's QTPP-enhanced eLoran delivers 
                on this mandate through <strong style={{ color: '#00d4ff' }}>public-private partnership</strong>, 
                leveraging existing decommissioned infrastructure.
              </p>

              <div style={{
                padding: '1.5rem',
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '12px',
                marginBottom: '2rem'
              }}>
                <h4 style={{
                  color: '#f59e0b',
                  fontWeight: '700',
                  marginBottom: '1rem',
                  fontSize: '1.1rem'
                }}>Why 8-20 Meters Matters</h4>
                <p style={{
                  color: '#8b949e',
                  lineHeight: '1.7',
                  marginBottom: '1rem'
                }}>
                  Standard eLoran provides <strong style={{ color: '#e6edf3' }}>sub-100 nanosecond timing</strong> but 
                  only ~500 meter positioning accuracy. QTPP enhancement achieves <strong style={{ color: '#f59e0b' }}>8-20 meter 
                  positioning</strong>"sufficient for:
                </p>
                <ul style={{
                  color: '#8b949e',
                  lineHeight: '2',
                  paddingLeft: '1.5rem'
                }}>
                  <li><strong style={{ color: '#e6edf3' }}>Lane-level navigation</strong> without cellular connectivity</li>
                  <li><strong style={{ color: '#e6edf3' }}>Port operations</strong> in RF-contested environments</li>
                  <li><strong style={{ color: '#e6edf3' }}>Agricultural automation</strong> in rural areas with no network</li>
                  <li><strong style={{ color: '#e6edf3' }}>Emergency response</strong> when cell towers are down</li>
                </ul>
              </div>

              <div style={{
                padding: '1.5rem',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '12px'
              }}>
                <h4 style={{
                  color: '#ef4444',
                  fontWeight: '700',
                  marginBottom: '0.75rem',
                  fontSize: '1rem'
                }}> Network-Independent Positioning</h4>
                <p style={{
                  color: '#8b949e',
                  lineHeight: '1.6',
                  fontSize: '0.95rem'
                }}>
                  Unlike A-GPS or RTK corrections that require cellular/internet connectivity, QTPP-enhanced eLoran 
                  operates <strong style={{ color: '#e6edf3' }}>completely independently</strong> — critical during 
                  disasters, in remote areas, or in adversarial jamming scenarios.
                </p>
              </div>
            </div>

            <div>
              {/* eLoran Coverage Map Placeholder - Clickable link to coverage map */}
              <div 
                onClick={() => { setShowOption('pilot'); scrollToSection('ci-dashboard'); }}
                style={{
                  background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)',
                  border: '1px solid rgba(0, 212, 255, 0.2)',
                  borderRadius: '16px',
                  padding: '2rem',
                  marginBottom: '1.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={e => { e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.5)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.2)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1.5rem'
                }}>
                  <h4 style={{ color: '#e6edf3', fontWeight: '600' }}>Planned Pilot Deployment</h4>
                  <div style={{
                    padding: '0.25rem 0.75rem',
                    background: 'rgba(0, 212, 255, 0.2)',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    color: '#00d4ff'
                  }}>20 Stations</div>
                </div>
                
                {/* Simplified Map Visualization - Orange dot moved to far left */}
                <div style={{
                  aspectRatio: '16/10',
                  background: 'rgba(10, 10, 15, 0.5)',
                  borderRadius: '12px',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {/* Coverage circles - Dana IN (Hub) moved to far left */}
                  {[
                    { x: '15%', y: '45%', name: 'Dana IN (Hub)', isHub: true },
                    { x: '55%', y: '35%', name: 'Seneca NY', isHub: false },
                    { x: '58%', y: '55%', name: 'Wildwood NJ', isHub: false },
                    { x: '60%', y: '65%', name: 'Carolina Beach NC', isHub: false }
                  ].map((site, i) => (
                    <div key={i} style={{
                      position: 'absolute',
                      left: site.x,
                      top: site.y,
                      transform: 'translate(-50%, -50%)'
                    }}>
                      <div style={{
                        width: site.isHub ? '120px' : '100px',
                        height: site.isHub ? '120px' : '100px',
                        background: `radial-gradient(circle, ${site.isHub ? 'rgba(245, 158, 11, 0.3)' : 'rgba(0, 212, 255, 0.3)'} 0%, transparent 70%)`,
                        borderRadius: '50%',
                        animation: 'pulse 2s ease-in-out infinite',
                        animationDelay: `${i * 0.5}s`
                      }} />
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: site.isHub ? '16px' : '12px',
                        height: site.isHub ? '16px' : '12px',
                        background: site.isHub ? '#f59e0b' : '#00d4ff',
                        borderRadius: '50%',
                        boxShadow: `0 0 15px ${site.isHub ? '#f59e0b' : '#00d4ff'}`
                      }} />
                      <div style={{
                        position: 'absolute',
                        top: site.isHub ? '70%' : '65%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '0.65rem',
                        color: site.isHub ? '#f59e0b' : '#00d4ff',
                        whiteSpace: 'nowrap',
                        fontWeight: '600'
                      }}>{site.name}</div>
                    </div>
                  ))}
                  <div style={{
                    position: 'absolute',
                    bottom: '1rem',
                    left: '1rem',
                    fontSize: '0.75rem',
                    color: '#8b949e'
                  }}>
                    15 CONUS + 5 Territories
                  </div>
                </div>
              </div>

              {/* Critical Systems Dependencies */}
              <div style={{ marginTop: '2rem' }}>
                <p style={{
                  color: '#8b949e',
                  lineHeight: '1.8',
                  marginBottom: '1.5rem',
                  fontSize: '1.05rem'
                }}>
                  Today's critical systems"power grids, financial markets, telecommunications, transportation"rely 
                  on GPS/GNSS for timing synchronization. This creates catastrophic single points of failure 
                  vulnerable to jamming, spoofing, and both intentional and natural disruption.
                </p>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '1rem'
                }}>
                  {[
                    { icon: '\u26A1', label: 'Power Grid Sync', desc: 'Phasor measurement units' },
                    { icon: '\uD83D\uDCB0', label: 'Financial Timestamping', desc: 'Sub-microsecond trades' },
                    { icon: '\uD83D\uDCE1', label: 'Telecom Networks', desc: '5G/6G synchronization' },
                    { icon: '\uD83D\uDEE1\uFE0F', label: 'Defense Systems', desc: 'Secure PNT operations' }
                  ].map((item, i) => (
                    <div key={i} style={{
                      padding: '1.25rem',
                      background: 'rgba(0, 212, 255, 0.03)',
                      border: '1px solid rgba(0, 212, 255, 0.1)',
                      borderRadius: '12px'
                    }}>
                      <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{item.icon}</div>
                      <div style={{ fontWeight: '600', color: '#e6edf3', marginBottom: '0.25rem' }}>{item.label}</div>
                      <div style={{ fontSize: '0.85rem', color: '#8b949e' }}>{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* U.S. eLoran Map Registry v7.25 */}
      <section id="ci-dashboard" style={{
        padding: '4rem 4rem',
        background: 'linear-gradient(180deg, transparent 0%, rgba(124, 58, 237, 0.03) 50%, transparent 100%)',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ 
              fontSize: '1.75rem', 
              fontWeight: 'bold', 
              textAlign: 'center', 
              marginBottom: '0.5rem', 
              background: 'linear-gradient(to right, #60a5fa, #22d3ee)', 
              WebkitBackgroundClip: 'text', 
              WebkitTextFillColor: 'transparent' 
            }}>
              U.S. eLoran Map Registry v7.25
            </h2>
            <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.9rem', margin: 0 }}>
              4 Pilot + 11 Expansion + 5 Territories = 20 Stations | QTPP Infrastructure | VisuTel, Inc.
            </p>
          </div>

          {/* Station Configuration Buttons */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            {buttonOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => setShowOption(opt.value)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  border: 'none',
                  backgroundColor: showOption === opt.value ? opt.color : '#334155',
                  color: 'white',
                  transition: 'all 0.2s'
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Controls Row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem', justifyContent: 'center', fontSize: '0.875rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={showCoverage} onChange={(e) => setShowCoverage(e.target.checked)} style={{ width: '1rem', height: '1rem' }} />
              <span style={{ color: '#e6edf3' }}>Coverage</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={showLabels} onChange={(e) => setShowLabels(e.target.checked)} style={{ width: '1rem', height: '1rem' }} />
              <span style={{ color: '#e6edf3' }}>Labels</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={showBaselines} onChange={(e) => setShowBaselines(e.target.checked)} style={{ width: '1rem', height: '1rem' }} />
              <span style={{ color: '#e6edf3' }}>Baselines</span>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ color: '#e6edf3' }}>Range:</span>
              <input 
                type="range" 
                min="400" 
                max="1500" 
                step="50"
                value={coverageRange} 
                onChange={(e) => setCoverageRange(Number(e.target.value))} 
                style={{ width: '6rem' }} 
              />
              <span style={{ fontFamily: 'monospace', color: '#22d3ee' }}>{coverageRange} km</span>
            </div>
          </div>

          {/* SVG Map */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} style={{ width: '100%', maxWidth: '900px', border: '1px solid #334155', borderRadius: '8px', backgroundColor: '#1e293b' }}>
              {/* US Outline */}
              <path d={usOutlinePath} fill="#1e293b" stroke="#475569" strokeWidth="1.5" />

              {/* Coverage circles */}
              {showCoverage && conusStations.map(station => (
                <circle
                  key={`coverage-${station.id}`}
                  cx={projectLon(station.lon)}
                  cy={projectLat(station.lat)}
                  r={kmToPixels(coverageRange)}
                  fill={getStationColor(station)}
                  fillOpacity="0.12"
                  stroke={getStationColor(station)}
                  strokeWidth="1"
                  strokeOpacity="0.4"
                  strokeDasharray="4,4"
                />
              ))}

              {/* Baselines */}
              {showBaselines && getBaselines().map((baseline, i) => {
                const fromStation = allStations.find(s => s.id === baseline.from);
                const toStation = allStations.find(s => s.id === baseline.to);
                if (!fromStation || !toStation) return null;
                if (!conusStations.find(s => s.id === baseline.from) || !conusStations.find(s => s.id === baseline.to)) return null;

                const midX = (projectLon(fromStation.lon) + projectLon(toStation.lon)) / 2;
                const midY = (projectLat(fromStation.lat) + projectLat(toStation.lat)) / 2;

                return (
                  <g key={`baseline-${i}`}>
                    <line
                      x1={projectLon(fromStation.lon)}
                      y1={projectLat(fromStation.lat)}
                      x2={projectLon(toStation.lon)}
                      y2={projectLat(toStation.lat)}
                      stroke={getBaselineColor(baseline.type)}
                      strokeWidth="2"
                      strokeOpacity="0.7"
                      strokeDasharray={baseline.type === 'long' ? '8,4' : '4,2'}
                    />
                    <text x={midX} y={midY - 5} fontSize="9" fill={getBaselineColor(baseline.type)} textAnchor="middle" fontWeight="bold">
                      {baseline.distance} km
                    </text>
                  </g>
                );
              })}

              {/* Station markers */}
              {conusStations.map(station => (
                <g key={station.id}>
                  <polygon
                    points={`
                      ${projectLon(station.lon)},${projectLat(station.lat) - 12}
                      ${projectLon(station.lon) - 7},${projectLat(station.lat) + 6}
                      ${projectLon(station.lon) + 7},${projectLat(station.lat) + 6}
                    `}
                    fill={getStationColor(station)}
                    stroke="white"
                    strokeWidth="1.5"
                  />
                  {showLabels && (
                    <text
                      x={projectLon(station.lon)}
                      y={projectLat(station.lat) + 20}
                      fontSize="9"
                      fill="white"
                      textAnchor="middle"
                      fontWeight="bold"
                    >
                      {station.name}, {station.state}
                    </text>
                  )}
                </g>
              ))}

              {/* Legend */}
              <g transform={`translate(10, ${showOption === 'territories' ? svgHeight - 215 : svgHeight - 130})`}>
                <rect x="0" y="0" width="175" height={showOption === 'territories' ? 125 : 120} fill="#0f172a" fillOpacity="0.95" rx="4" stroke="#334155" strokeWidth="1" />
                <text x="10" y="18" fontSize="11" fill="#fff" fontWeight="bold">Legend — Registry v7.25</text>
                <polygon points="20,38 14,50 26,50" fill="#10b981" stroke="#fff" strokeWidth="1" />
                <text x="35" y="47" fontSize="9" fill="#94a3b8">Pilot Sites (4)</text>
                <polygon points="20,60 14,72 26,72" fill="#3b82f6" stroke="#fff" strokeWidth="1" />
                <text x="35" y="69" fontSize="9" fill="#94a3b8">CONUS Expansion (11)</text>
                <line x1="10" y1="88" x2="30" y2="88" stroke="#22c55e" strokeWidth="2" />
                <text x="35" y="92" fontSize="9" fill="#94a3b8">Optimal Baseline</text>
                <line x1="10" y1="106" x2="30" y2="106" stroke="#ef4444" strokeWidth="2" strokeDasharray="6,3" />
                <text x="35" y="110" fontSize="9" fill="#94a3b8">Long Baseline (&gt;1200km)</text>
                {showOption === 'territories' && (
                  <>
                    <polygon points="20,124 14,136 26,136" fill="#f43f5e" stroke="#fff" strokeWidth="1" />
                    <text x="35" y="133" fontSize="9" fill="#94a3b8">Territory Stations (5)</text>
                  </>
                )}
              </g>

              {/* Station count badge */}
              <g transform={`translate(${svgWidth - 130}, 15)`}>
                <rect x="0" y="0" width="120" height="40" fill="#0f172a" fillOpacity="0.95" rx="4" stroke="#06b6d4" strokeWidth="1" />
                <text x="60" y="18" fontSize="10" fill="#06b6d4" textAnchor="middle" fontWeight="bold">STATIONS</text>
                <text x="60" y="34" fontSize="16" fill="#fff" textAnchor="middle" fontWeight="bold">{stats.count}</text>
              </g>

              {/* Territory Insets */}
              {showOption === 'territories' && (
                <>
                  {/* Alaska Inset */}
                  <g transform="translate(10, 510)">
                    <rect x="0" y="0" width="180" height="80" fill="#1e293b" stroke="#f43f5e" strokeWidth="1.5" rx="4" />
                    <text x="90" y="15" fontSize="10" fill="#f43f5e" textAnchor="middle" fontWeight="bold">ALASKA</text>
                    <path d="M 20,35 L 35,25 L 60,28 L 90,22 L 120,30 L 140,28 L 160,35 L 155,50 L 130,55 L 100,50 L 70,55 L 40,50 L 25,45 Z" fill="#334155" stroke="#475569" strokeWidth="1" />
                    <polygon points="110,48 106,56 114,56" fill="#f43f5e" stroke="#fff" strokeWidth="1" />
                    <text x="110" y="68" fontSize="7" fill="#fff" textAnchor="middle">Kodiak</text>
                    <polygon points="55,30 51,38 59,38" fill="#f43f5e" stroke="#fff" strokeWidth="1" />
                    <text x="55" y="22" fontSize="7" fill="#fff" textAnchor="middle">Pt. Clarence</text>
                    <circle cx="110" cy="52" r="25" fill="#f43f5e" fillOpacity="0.15" stroke="#f43f5e" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="2,2" />
                    <circle cx="55" cy="34" r="25" fill="#f43f5e" fillOpacity="0.15" stroke="#f43f5e" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="2,2" />
                  </g>

                  {/* Hawaii Inset */}
                  <g transform="translate(200, 510)">
                    <rect x="0" y="0" width="140" height="80" fill="#1e293b" stroke="#f43f5e" strokeWidth="1.5" rx="4" />
                    <text x="70" y="15" fontSize="10" fill="#f43f5e" textAnchor="middle" fontWeight="bold">HAWAII</text>
                    <ellipse cx="100" cy="50" rx="20" ry="15" fill="#334155" stroke="#475569" strokeWidth="1" />
                    <ellipse cx="70" cy="45" rx="10" ry="8" fill="#334155" stroke="#475569" strokeWidth="1" />
                    <ellipse cx="45" cy="40" rx="8" ry="6" fill="#334155" stroke="#475569" strokeWidth="1" />
                    <ellipse cx="25" cy="35" rx="6" ry="5" fill="#334155" stroke="#475569" strokeWidth="1" />
                    <polygon points="90,42 86,50 94,50" fill="#f43f5e" stroke="#fff" strokeWidth="1" />
                    <text x="90" y="62" fontSize="7" fill="#fff" textAnchor="middle">Upolu Pt.</text>
                    <circle cx="90" cy="46" r="30" fill="#f43f5e" fillOpacity="0.15" stroke="#f43f5e" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="2,2" />
                  </g>

                  {/* Puerto Rico Inset */}
                  <g transform="translate(350, 510)">
                    <rect x="0" y="0" width="140" height="80" fill="#1e293b" stroke="#f43f5e" strokeWidth="1.5" rx="4" />
                    <text x="70" y="15" fontSize="10" fill="#f43f5e" textAnchor="middle" fontWeight="bold">PUERTO RICO</text>
                    <path d="M 30,45 L 50,40 L 80,38 L 110,42 L 115,50 L 105,55 L 70,58 L 40,55 L 30,50 Z" fill="#334155" stroke="#475569" strokeWidth="1" />
                    <polygon points="35,45 31,53 39,53" fill="#f43f5e" stroke="#fff" strokeWidth="1" />
                    <text x="35" y="65" fontSize="7" fill="#fff" textAnchor="middle">Aguadilla</text>
                    <circle cx="35" cy="49" r="28" fill="#f43f5e" fillOpacity="0.15" stroke="#f43f5e" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="2,2" />
                  </g>

                  {/* Guam Inset */}
                  <g transform="translate(500, 510)">
                    <rect x="0" y="0" width="120" height="80" fill="#1e293b" stroke="#f43f5e" strokeWidth="1.5" rx="4" />
                    <text x="60" y="15" fontSize="10" fill="#f43f5e" textAnchor="middle" fontWeight="bold">GUAM</text>
                    <path d="M 55,25 L 65,30 L 68,45 L 65,60 L 55,68 L 50,60 L 48,45 L 52,30 Z" fill="#334155" stroke="#475569" strokeWidth="1" />
                    <polygon points="58,42 54,50 62,50" fill="#f43f5e" stroke="#fff" strokeWidth="1" />
                    <text x="58" y="62" fontSize="7" fill="#fff" textAnchor="middle">Guam</text>
                    <circle cx="58" cy="46" r="25" fill="#f43f5e" fillOpacity="0.15" stroke="#f43f5e" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="2,2" />
                  </g>
                </>
              )}
            </svg>
          </div>

          {/* Stats Panel */}
          <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '0.75rem', maxWidth: '700px', margin: '1rem auto 0' }}>
            <div style={{ backgroundColor: '#1e293b', padding: '0.75rem', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#22d3ee' }}>{stats.count}</div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Total Stations</div>
            </div>
            <div style={{ backgroundColor: '#1e293b', padding: '0.75rem', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#60a5fa' }}>{stats.metros}</div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Top 50 Metros</div>
            </div>
            <div style={{ backgroundColor: '#1e293b', padding: '0.75rem', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#4ade80' }}>{stats.conus}</div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>CONUS Landmass</div>
            </div>
            <div style={{ backgroundColor: '#1e293b', padding: '0.75rem', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: showOption === 'full' || showOption === 'territories' ? '#4ade80' : '#fbbf24' }}>{stats.gap}</div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Coverage Gap</div>
            </div>
          </div>

          {/* Recommendation Panel */}
          <div style={{ marginTop: '1rem', maxWidth: '700px', margin: '1rem auto 0', backgroundColor: '#1e293b', borderRadius: '8px', padding: '1rem' }}>
            <h3 style={{ fontWeight: 'bold', marginBottom: '0.5rem', textAlign: 'center', fontSize: '0.9rem', color: '#e6edf3' }}>
              {showOption === 'pilot' && ' Phase 1 Pilot: East Coast + Midwest Hub (4 Stations) " $6-12M'}
              {showOption === 'full' && ' Full CONUS (15 Stations): 98% Coverage " $34-56M'}
              {showOption === 'territories' && ' Full National + Territories (20 Stations): Strategic Coverage " $45-70M'}
            </h3>
            <div style={{ fontSize: '0.8rem', color: '#cbd5e1' }}>
              {showOption === 'pilot' && (
                <p style={{ margin: 0 }}><strong>Seneca, NY + Wildwood, NJ + Carolina Beach, NC + Dana, IN (Hub)</strong> " Establishes East Coast timing backbone plus Great Lakes Master hub covering 120M+ population from Boston to Chicago. Dana provides critical Midwest anchor.</p>
              )}
              {showOption === 'full' && (
                <p style={{ margin: 0 }}><strong>Registry v7.25: 4 Pilot + 11 CONUS Expansion</strong> —  Complete coverage including SW Minnesota (North Central gap fill), Searchlight (Las Vegas), and all legacy USCG sites. Achieves 98%+ CONUS coverage with optimal geometry.</p>
              )}
              {showOption === 'territories' && (
                <p style={{ margin: 0 }}><strong>CONUS (15) + Alaska (2) + Hawaii (1) + Puerto Rico (1) + Guam (1)</strong> " Full national coverage including strategic Indo-Pacific presence. Kodiak & Port Clarence cover Alaska; Upolu Point covers Hawaiian Islands; Aguadilla covers Caribbean; Guam provides Western Pacific anchor.</p>
              )}
            </div>
          </div>

          {/* Station List */}
          <div style={{ marginTop: '1rem', maxWidth: '700px', margin: '1rem auto 0', backgroundColor: 'rgba(30, 41, 59, 0.5)', borderRadius: '8px', padding: '1rem' }}>
            <h4 style={{ fontWeight: 'bold', fontSize: '0.875rem', marginBottom: '0.75rem', color: '#22d3ee' }}>Active Stations ({stats.count})</h4>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: '0.5rem', fontSize: '0.75rem' }}>
              {visibleStations.map(station => (
                <div 
                  key={station.id}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    padding: '0.5rem', 
                    borderRadius: '6px',
                    backgroundColor: `${getStationColor(station)}20`, 
                    border: `1px solid ${getStationColor(station)}40` 
                  }}
                >
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: getStationColor(station), flexShrink: 0 }} />
                  <span style={{ fontWeight: '500', color: '#e6edf3', fontSize: isMobile ? '0.7rem' : '0.75rem' }}>{station.name}, {station.state}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div style={{ marginTop: '1rem', fontSize: '0.7rem', color: '#64748b', textAlign: 'center' }}>
            <p style={{ margin: 0 }}>Baseline distances: Optimal 600"1,000 km | Acceptable 1,000"1,200 km | Long &gt;1,200 km</p>
            <p style={{ margin: '0.25rem 0 0' }}>Pilot (4): Seneca, Wildwood, Carolina Beach, Dana (hub) | Expansion (11): Malone, Rayville, Raymondville, SW MN, George, Fallon, Searchlight, Havre, Gillette, Boise City, Las Cruces | Territories (5): AK(2), HI, PR, GU</p>
          </div>
        </div>
      </section>


      {/* Section 04: GMVS & Operational Intelligence */}
      <section id="dashboard" style={{
        padding: isMobile ? '3rem 1.5rem' : '6rem 4rem',
        background: 'linear-gradient(180deg, transparent 0%, rgba(124, 58, 237, 0.03) 50%, transparent 100%)',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
            <span style={{
              fontSize: isMobile ? '3rem' : '5rem',
              fontWeight: '800',
              color: 'rgba(0, 212, 255, 0.15)',
              lineHeight: '1',
              letterSpacing: '-4px'
            }}>04</span>
            <div>
              <div style={{ color: '#00d4ff', fontSize: '0.8rem', letterSpacing: '3px', marginBottom: '0.5rem' }}>GNSS MONITORING & VERIFICATION</div>
              <h2 style={{ fontSize: isMobile ? '1.5rem' : '2.5rem', fontWeight: '700', letterSpacing: '-1px' }}>QTPP Operational Intelligence</h2>
            </div>
          </div>

          <p style={{
            color: '#8b949e',
            fontSize: '1.15rem',
            maxWidth: '800px',
            marginBottom: '3rem',
            lineHeight: '1.7'
          }}>
            The <strong style={{ color: '#e6edf3' }}>GNSS Monitoring and Verification Service (GMVS)</strong> provides 
            comprehensive situational awareness through the Three A's framework"transforming raw timing data 
            into actionable intelligence.
          </p>

          {/* Three A's Framework */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '2rem',
            marginBottom: '4rem'
          }}>
            {[
              {
                letter: 'A',
                title: 'Awareness',
                subtitle: 'GMVS',
                color: '#00d4ff',
                description: 'Real-time monitoring of GNSS signal integrity, jamming detection, and spoofing identification across the entire infrastructure.',
                features: ['Signal Quality Monitoring', 'Anomaly Detection', 'Threat Classification', 'Geographic Mapping']
              },
              {
                letter: 'A',
                title: 'Assurance',
                subtitle: 'TPO-V',
                color: '#7c3aed',
                description: 'Time Position Object Verification provides cryptographic proof of timing authenticity through physics-gated trust mechanisms.',
                features: ['Cryptographic Timestamps', 'Chain of Custody', 'Tamper Evidence', 'Audit Trails']
              },
              {
                letter: 'A',
                title: 'Availability',
                subtitle: 'LDC',
                color: '#10b981',
                description: 'Local Distribution Centers ensure continuous timing services through hierarchical redundancy and automatic failover.',
                features: ['99.999% Uptime', 'Automatic Failover', 'Multi-Path Redundancy', 'Graceful Degradation']
              }
            ].map((item, i) => (
              <div key={i} style={{
                background: `linear-gradient(135deg, ${item.color}10 0%, transparent 100%)`,
                border: `1px solid ${item.color}30`,
                borderRadius: '20px',
                padding: '2rem',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-20px',
                  right: '-10px',
                  fontSize: '8rem',
                  fontWeight: '900',
                  color: `${item.color}10`,
                  lineHeight: '1'
                }}>{item.letter}</div>
                
                <div style={{
                  fontSize: '0.7rem',
                  color: item.color,
                  letterSpacing: '2px',
                  marginBottom: '0.5rem',
                  fontWeight: '600'
                }}>{item.subtitle}</div>
                
                <h3 style={{
                  fontSize: '1.75rem',
                  fontWeight: '700',
                  color: '#e6edf3',
                  marginBottom: '1rem'
                }}>{item.title}</h3>
                
                <p style={{
                  color: '#8b949e',
                  lineHeight: '1.7',
                  marginBottom: '1.5rem',
                  fontSize: '0.95rem'
                }}>{item.description}</p>
                
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem'
                }}>
                  {item.features.map((feature, fi) => (
                    <span key={fi} style={{
                      padding: '0.35rem 0.75rem',
                      background: `${item.color}15`,
                      border: `1px solid ${item.color}30`,
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      color: item.color
                    }}>{feature}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Dashboard Preview */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(10, 15, 26, 0.9) 0%, rgba(10, 10, 15, 0.95) 100%)',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            borderRadius: '20px',
            padding: isMobile ? '1rem' : '1.5rem',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
          }}>
            {/* Dashboard Header */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: isMobile ? '1rem' : '0',
              marginBottom: '1.5rem',
              paddingBottom: '1rem',
              borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.5rem' : '1rem', flexWrap: 'wrap' }}>
                <div style={{
                  width: '10px',
                  height: '10px',
                  background: '#10b981',
                  borderRadius: '50%',
                  boxShadow: '0 0 10px #10b981'
                }} />
                <span style={{ fontWeight: '600', color: '#e6edf3', fontSize: isMobile ? '0.85rem' : '1rem' }}>QTPP Dashboard</span>
                <span style={{
                  padding: '0.25rem 0.75rem',
                  background: 'rgba(16, 185, 129, 0.2)',
                  borderRadius: '20px',
                  fontSize: '0.7rem',
                  color: '#10b981'
                }}>LIVE</span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {['coverage', 'timing', 'threats'].map((tab) => (
                  <button key={tab} onClick={() => setDashboardMetric(tab)} style={{
                    padding: isMobile ? '0.4rem 0.75rem' : '0.5rem 1rem',
                    background: dashboardMetric === tab ? 'rgba(0, 212, 255, 0.2)' : 'transparent',
                    border: `1px solid ${dashboardMetric === tab ? '#00d4ff' : 'rgba(0, 212, 255, 0.2)'}`,
                    borderRadius: '8px',
                    color: dashboardMetric === tab ? '#00d4ff' : '#8b949e',
                    fontSize: isMobile ? '0.7rem' : '0.8rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                    transition: 'all 0.2s'
                  }}>{tab}</button>
                ))}
              </div>
            </div>

            {/* Dashboard Content */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
              gap: '1.5rem'
            }}>
              {/* Main Visualization */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.3)',
                borderRadius: '12px',
                padding: isMobile ? '1rem' : '1.5rem',
                minHeight: isMobile ? '250px' : '300px',
                position: 'relative'
              }}>
                <h4 style={{
                  fontSize: '0.85rem',
                  color: '#8b949e',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  {dashboardMetric === 'coverage' && 'eLoran Coverage with RF Range Controls'}
                  {dashboardMetric === 'timing' && 'Timing Accuracy Distribution'}
                  {dashboardMetric === 'threats' && (
                    <>
                      <span>Threat Detection Map</span>
                      <span style={{
                        background: 'rgba(239, 68, 68, 0.25)',
                        border: '2px solid #ef4444',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '0.7rem',
                        color: '#ef4444',
                        fontWeight: '700'
                      }}>3 ACTIVE THREATS</span>
                    </>
                  )}
                </h4>
                
                {/* Simulated Chart/Map */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  {dashboardMetric === 'coverage' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {/* Bar Graph */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'space-around',
                        height: '120px',
                        gap: '0.5rem',
                        padding: '0 5px'
                      }}>
                        {[
                          { region: 1, coverage: 94 },
                          { region: 2, coverage: 97 },
                          { region: 3, coverage: 96 },
                          { region: 4, coverage: 91 },
                          { region: 5, coverage: 93 },
                          { region: 6, coverage: 88 },
                          { region: 7, coverage: 85 },
                          { region: 8, coverage: 78 },
                          { region: 9, coverage: 92 },
                          { region: 10, coverage: 82 }
                        ].map((item) => (
                          <div key={item.region} style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flex: 1
                          }}>
                            <span style={{
                              fontSize: '0.6rem',
                              color: '#8b949e',
                              marginBottom: '2px'
                            }}>{item.coverage}%</span>
                            <div style={{
                              width: '100%',
                              height: `${item.coverage}%`,
                              background: `linear-gradient(180deg, #00d4ff 0%, ${item.coverage > 90 ? '#10b981' : item.coverage > 80 ? '#00d4ff' : '#f59e0b'} 100%)`,
                              borderRadius: '3px 3px 0 0',
                              transition: 'height 0.5s ease',
                              minHeight: '20px'
                            }} />
                            <span style={{
                              fontSize: '0.65rem',
                              color: '#e6edf3',
                              fontWeight: '600',
                              marginTop: '4px'
                            }}>{item.region}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ textAlign: 'center', fontSize: '0.65rem', color: '#8b949e' }}>FEMA Region</div>
                      
                      {/* FEMA Region Legend */}
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.4)',
                        borderRadius: '8px',
                        padding: '10px',
                        fontSize: '0.6rem'
                      }}>
                        <div style={{ fontWeight: '700', color: '#00d4ff', marginBottom: '6px', fontSize: '0.7rem' }}>FEMA Regions by State</div>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(5, 1fr)',
                          gap: '6px'
                        }}>
                          <div><span style={{ color: '#10b981', fontWeight: '600' }}>1:</span> <span style={{ color: '#8b949e' }}>CT, MA, ME, NH, RI, VT</span></div>
                          <div><span style={{ color: '#10b981', fontWeight: '600' }}>2:</span> <span style={{ color: '#8b949e' }}>NJ, NY, PR, VI</span></div>
                          <div><span style={{ color: '#10b981', fontWeight: '600' }}>3:</span> <span style={{ color: '#8b949e' }}>DC, DE, MD, PA, VA, WV</span></div>
                          <div><span style={{ color: '#10b981', fontWeight: '600' }}>4:</span> <span style={{ color: '#8b949e' }}>AL, FL, GA, KY, MS, NC, SC, TN</span></div>
                          <div><span style={{ color: '#10b981', fontWeight: '600' }}>5:</span> <span style={{ color: '#8b949e' }}>IL, IN, MI, MN, OH, WI</span></div>
                          <div><span style={{ color: '#00d4ff', fontWeight: '600' }}>6:</span> <span style={{ color: '#8b949e' }}>AR, LA, NM, OK, TX</span></div>
                          <div><span style={{ color: '#00d4ff', fontWeight: '600' }}>7:</span> <span style={{ color: '#8b949e' }}>IA, KS, MO, NE</span></div>
                          <div><span style={{ color: '#f59e0b', fontWeight: '600' }}>8:</span> <span style={{ color: '#8b949e' }}>CO, MT, ND, SD, UT, WY</span></div>
                          <div><span style={{ color: '#10b981', fontWeight: '600' }}>9:</span> <span style={{ color: '#8b949e' }}>AZ, CA, HI, NV, GU</span></div>
                          <div><span style={{ color: '#00d4ff', fontWeight: '600' }}>10:</span> <span style={{ color: '#8b949e' }}>AK, ID, OR, WA</span></div>
                        </div>
                      </div>
                      
                      {/* Critical Infrastructure by Region */}
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.4)',
                        borderRadius: '8px',
                        padding: '10px',
                        fontSize: '0.6rem'
                      }}>
                        <div style={{ fontWeight: '700', color: '#00d4ff', marginBottom: '6px', fontSize: '0.7rem' }}>Critical Infrastructure by FEMA Region</div>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: '6px'
                        }}>
                          <div><span style={{ color: '#e6edf3', fontWeight: '600' }}>1:</span> <span style={{ color: '#8b949e' }}>Port of Boston, ISO-NE Grid, Financial (Boston)</span></div>
                          <div><span style={{ color: '#e6edf3', fontWeight: '600' }}>2:</span> <span style={{ color: '#8b949e' }}>Port of NY/NJ, NYSE, NYISO Grid, Wall St</span></div>
                          <div><span style={{ color: '#e6edf3', fontWeight: '600' }}>3:</span> <span style={{ color: '#8b949e' }}>Port of Baltimore, PJM Grid, Federal DC</span></div>
                          <div><span style={{ color: '#e6edf3', fontWeight: '600' }}>4:</span> <span style={{ color: '#8b949e' }}>Port of Savannah, TVA Grid, CDC Atlanta</span></div>
                          <div><span style={{ color: '#e6edf3', fontWeight: '600' }}>5:</span> <span style={{ color: '#8b949e' }}>Port of Chicago, MISO Grid, CME Chicago</span></div>
                          <div><span style={{ color: '#e6edf3', fontWeight: '600' }}>6:</span> <span style={{ color: '#8b949e' }}>Port of Houston, ERCOT Grid, Oil/Gas Hubs</span></div>
                          <div><span style={{ color: '#e6edf3', fontWeight: '600' }}>7:</span> <span style={{ color: '#8b949e' }}>SPP Grid, Ag Data Centers, Telecom Hubs</span></div>
                          <div><span style={{ color: '#e6edf3', fontWeight: '600' }}>8:</span> <span style={{ color: '#8b949e' }}>WAPA Grid, Mining Ops, NORAD Colorado</span></div>
                          <div><span style={{ color: '#e6edf3', fontWeight: '600' }}>9:</span> <span style={{ color: '#8b949e' }}>Port of LA/Long Beach, CAISO Grid, Silicon Valley</span></div>
                          <div><span style={{ color: '#e6edf3', fontWeight: '600' }}>10:</span> <span style={{ color: '#8b949e' }}>Port of Seattle, BPA Grid, Tech (Seattle)</span></div>
                        </div>
                      </div>
                    </div>
                  )}
                  {dashboardMetric === 'timing' && (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div style={{
                        width: '200px',
                        height: '200px',
                        borderRadius: '50%',
                        background: 'conic-gradient(#10b981 0% 65%, #00d4ff 65% 90%, #f59e0b 90% 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <div style={{
                          width: '140px',
                          height: '140px',
                          borderRadius: '50%',
                          background: 'rgba(10, 10, 15, 0.95)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <div style={{ fontSize: '2rem', fontWeight: '800', color: '#10b981' }}>&lt;50ns</div>
                          <div style={{ fontSize: '0.75rem', color: '#8b949e' }}>Avg Accuracy</div>
                        </div>
                      </div>
                    </div>
                  )}
                  {dashboardMetric === 'threats' && (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      {/* Map area with threats */}
                      <div style={{
                        position: 'relative',
                        margin: '0 10px',
                        border: '1px solid rgba(0, 212, 255, 0.2)',
                        borderRadius: '8px',
                        background: 'rgba(0, 0, 0, 0.2)',
                        height: '130px'
                      }}>
                        {/* Threat markers */}
                        {[
                          { x: 18, y: 50, type: 'JAMMING', location: 'Port of Los Angeles', severity: 'high', desc: 'Active RF interference' },
                          { x: 50, y: 50, type: 'SPOOFING', location: 'Gulf Coast / Houston', severity: 'medium', desc: 'GPS position anomaly' },
                          { x: 82, y: 50, type: 'ANOMALY', location: 'NYC Metro Area', severity: 'low', desc: 'Signal degradation' }
                        ].map((threat, i) => (
                          <div key={i} style={{
                            position: 'absolute',
                            left: `${threat.x}%`,
                            top: `${threat.y}%`,
                            transform: 'translate(-50%, -50%)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <div style={{
                              width: '45px',
                              height: '45px',
                              background: `radial-gradient(circle, ${threat.severity === 'high' ? '#ef4444' : threat.severity === 'medium' ? '#fbbf24' : '#00d4ff'}70 0%, transparent 70%)`,
                              borderRadius: '50%',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <div style={{
                                width: '14px',
                                height: '14px',
                                background: threat.severity === 'high' ? '#ef4444' : threat.severity === 'medium' ? '#fbbf24' : '#00d4ff',
                                borderRadius: '50%',
                                border: '2px solid white',
                                boxShadow: `0 0 12px ${threat.severity === 'high' ? '#ef4444' : threat.severity === 'medium' ? '#fbbf24' : '#00d4ff'}`
                              }} />
                            </div>
                            <div style={{ marginTop: '3px', fontSize: '0.65rem', fontWeight: '700', color: threat.severity === 'high' ? '#ef4444' : threat.severity === 'medium' ? '#fbbf24' : '#00d4ff', whiteSpace: 'nowrap', textAlign: 'center' }}>{threat.type}</div>
                            <div style={{ fontSize: '0.6rem', fontWeight: '600', color: '#e6edf3', whiteSpace: 'nowrap', textAlign: 'center' }}>{threat.location}</div>
                            <div style={{ fontSize: '0.55rem', color: '#94a3b8', whiteSpace: 'nowrap', fontStyle: 'italic', textAlign: 'center' }}>{threat.desc}</div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Legend - Threat Severity on same line */}
                      <div style={{
                        margin: '8px 10px 0',
                        padding: '6px 10px',
                        background: 'rgba(0, 0, 0, 0.7)',
                        borderRadius: '6px',
                        fontSize: '0.65rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <span style={{ fontWeight: '600', color: '#e6edf3' }}>Threat Severity</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 6px #ef4444' }} />
                          <span style={{ color: '#ef4444', fontWeight: '600' }}>High</span>
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#fbbf24', boxShadow: '0 0 6px #fbbf24' }} />
                          <span style={{ color: '#fbbf24', fontWeight: '600' }}>Medium</span>
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#00d4ff', boxShadow: '0 0 6px #00d4ff' }} />
                          <span style={{ color: '#00d4ff', fontWeight: '600' }}>Low</span>
                        </span>
                      </div>
                      
                      {/* Verbiage - under the legend */}
                      <div style={{
                        margin: '12px 10px 8px',
                        padding: '8px 10px',
                        background: 'rgba(0, 212, 255, 0.1)',
                        border: '1px solid rgba(0, 212, 255, 0.3)',
                        borderRadius: '6px',
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        color: '#e6edf3',
                        lineHeight: '1.4',
                        textAlign: 'center'
                      }}>
                        This represents how QTPP provides situational awareness of GPS threats to critical infrastructure operators"detecting jamming attacks, spoofing attempts, and signal anomalies in real-time.
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Side Metrics */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                {[
                  { label: 'Active Stations', value: '18/20', color: '#10b981', icon: '\u25C9' },
                  { label: 'QBER Status', value: '< 3%', color: '#00d4ff', icon: '\u2B21' },
                  { label: 'Timing Accuracy', value: '< 50ns', color: '#7c3aed', icon: '\u25F7' },
                  { label: 'Threats Detected', value: '3', color: '#f59e0b', icon: '\u25B2' },
                  { label: 'TPOs Generated', value: '1.2M/day', color: '#00d4ff', icon: '\u25EB' }
                ].map((metric, i) => (
                  <div key={i} style={{
                    padding: '1rem',
                    background: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '10px',
                    borderLeft: `3px solid ${metric.color}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#8b949e', marginBottom: '0.25rem' }}>{metric.label}</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '700', color: metric.color }}>{metric.value}</div>
                    </div>
                    <span style={{ fontSize: '1.5rem' }}>{metric.icon}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 05: Why Network-Independent Matters */}
      <section style={{
        padding: isMobile ? '3rem 1.5rem' : '6rem 4rem',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '3rem' }}>
            <span style={{
              fontSize: isMobile ? '3rem' : '5rem',
              fontWeight: '800',
              color: 'rgba(0, 212, 255, 0.15)',
              lineHeight: '1',
              letterSpacing: '-4px'
            }}>05</span>
            <div>
              <div style={{ color: '#00d4ff', fontSize: '0.8rem', letterSpacing: '3px', marginBottom: '0.5rem' }}>RESILIENCE</div>
              <h2 style={{ fontSize: isMobile ? '1.5rem' : '2.5rem', fontWeight: '700', letterSpacing: '-1px' }}>Why 8-20 Meters Without Network Connectivity Matters</h2>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '3rem'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(0, 0, 0, 0.2) 100%)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '20px',
              padding: '2rem'
            }}>
              <h3 style={{
                color: '#ef4444',
                fontSize: '1.25rem',
                fontWeight: '700',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <span style={{ fontSize: '1.5rem' }}>{'\u2717'}</span>
                Current GPS Dependency
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {[
                  { problem: 'A-GPS / RTK requires cellular data', impact: 'Fails during disasters when towers are down' },
                  { problem: 'PPP corrections need internet', impact: 'Unavailable in remote/maritime areas' },
                  { problem: 'GPS signals easily jammed', impact: '< $50 devices can deny service for miles' },
                  { problem: 'Spoofing increasingly sophisticated', impact: 'Can redirect ships, drones, vehicles' }
                ].map((item, i) => (
                  <div key={i} style={{
                    padding: '1rem',
                    background: 'rgba(239, 68, 68, 0.05)',
                    borderRadius: '10px',
                    borderLeft: '3px solid #ef4444'
                  }}>
                    <div style={{ fontWeight: '600', color: '#e6edf3', marginBottom: '0.25rem' }}>{item.problem}</div>
                    <div style={{ fontSize: '0.85rem', color: '#8b949e' }}>{item.impact}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(0, 0, 0, 0.2) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '20px',
              padding: '2rem'
            }}>
              <h3 style={{
                color: '#10b981',
                fontSize: '1.25rem',
                fontWeight: '700',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <span style={{ fontSize: '1.5rem' }}>{'\u2713'}</span>
                QTPP-Enhanced eLoran
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {[
                  { solution: '100 kHz signals penetrate buildings', benefit: 'Works indoors, underground, urban canyons' },
                  { solution: 'No network connection required', benefit: 'Operates in complete communication blackout' },
                  { solution: 'Extremely jam-resistant', benefit: 'Would require nation-state level interference' },
                  { solution: 'Cryptographic authentication', benefit: 'Physics-gated trust eliminates spoofing' }
                ].map((item, i) => (
                  <div key={i} style={{
                    padding: '1rem',
                    background: 'rgba(16, 185, 129, 0.05)',
                    borderRadius: '10px',
                    borderLeft: '3px solid #10b981'
                  }}>
                    <div style={{ fontWeight: '600', color: '#e6edf3', marginBottom: '0.25rem' }}>{item.solution}</div>
                    <div style={{ fontSize: '0.85rem', color: '#8b949e' }}>{item.benefit}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Use Cases */}
          <div style={{
            marginTop: '3rem',
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: '1.5rem'
          }}>
            {[
              { icon: '🚢', title: 'Maritime Ports', desc: 'Port of New York & New Jersey for contested RF environments', accuracy: '8-12m' },
              { icon: '🚗', title: 'Autonomous Vehicles', desc: 'Lane-level positioning in tunnels & garages', accuracy: '< 10m' },
              { icon: '🌾', title: 'Precision Agriculture', desc: 'Remote areas with no cellular coverage', accuracy: '15-20m' },
              { icon: '🏥', title: 'Emergency Response', desc: 'When cell towers fail during disasters', accuracy: '10-15m' }
            ].map((useCase, i) => (
              <div key={i} style={{
                padding: '1.5rem',
                background: 'rgba(0, 212, 255, 0.03)',
                border: '1px solid rgba(0, 212, 255, 0.1)',
                borderRadius: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>{useCase.icon}</div>
                <h4 style={{ fontWeight: '600', color: '#e6edf3', marginBottom: '0.5rem' }}>{useCase.title}</h4>
                <p style={{ fontSize: '0.85rem', color: '#8b949e', marginBottom: '1rem', lineHeight: '1.5' }}>{useCase.desc}</p>
                <div style={{
                  display: 'inline-block',
                  padding: '0.35rem 0.75rem',
                  background: 'rgba(16, 185, 129, 0.2)',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  color: '#10b981',
                  fontWeight: '600'
                }}>{useCase.accuracy}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / Pre-Register Section */}
      <section id="contact" style={{
        padding: isMobile ? '3rem 1.5rem' : '6rem 4rem',
        background: 'linear-gradient(180deg, transparent 0%, rgba(0, 212, 255, 0.05) 100%)',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            display: 'inline-block',
            padding: '0.5rem 1rem',
            background: 'rgba(124, 58, 237, 0.1)',
            border: '1px solid rgba(124, 58, 237, 0.3)',
            borderRadius: '50px',
            marginBottom: '1.5rem',
            fontSize: '0.8rem',
            color: '#7c3aed',
            letterSpacing: '1px'
          }}>
            PRE-LAUNCH
          </div>
          
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            marginBottom: '1rem',
            letterSpacing: '-1px'
          }}>
            Be First to Access QTPP Technology
          </h2>
          
          <p style={{
            color: '#8b949e',
            fontSize: '1.1rem',
            marginBottom: '2.5rem',
            lineHeight: '1.7'
          }}>
            Join our pre-launch list for early access to technical documentation, 
            partnership opportunities, and pilot program details.
          </p>

          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '1rem',
            justifyContent: 'center',
            marginBottom: '3rem'
          }}>
            <input
              type="email"
              placeholder="Enter your email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              style={{
                padding: '1rem 1.5rem',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                borderRadius: '10px',
                color: '#e6edf3',
                fontSize: '1rem',
                width: isMobile ? '100%' : '300px',
                outline: 'none'
              }}
            />
            <button 
              onClick={() => {
                if (emailInput && emailInput.includes('@')) {
                  // Backend integration point - replace with your API call
                  console.log('Pre-register email:', emailInput);
                  alert('Thank you for pre-registering! We will contact you at: ' + emailInput);
                  setEmailInput('');
                } else {
                  alert('Please enter a valid email address');
                }
              }}
              style={{
                padding: '1rem 2rem',
                background: 'linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)',
                border: 'none',
                borderRadius: '10px',
                color: '#0a0a0f',
                fontWeight: '700',
                fontSize: '1rem',
                cursor: 'pointer'
              }}>
              Pre-Register
            </button>
          </div>

          {/* Contact Info */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'center',
            gap: isMobile ? '1.5rem' : '3rem',
            flexWrap: 'wrap'
          }}>
            {[
              { label: 'General Inquiries', email: 'info@visutelinc.com' },
              { label: 'Partnerships', email: 'partners@visutelinc.com' }
            ].map((contact, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.75rem', color: '#8b949e', marginBottom: '0.25rem' }}>{contact.label}</div>
                <div style={{ color: '#00d4ff', fontWeight: '500' }}>{contact.email}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: isMobile ? '2rem 1.5rem' : '3rem 4rem',
        borderTop: '1px solid rgba(0, 212, 255, 0.1)',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: isMobile ? '1.5rem' : '0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '36px',
              height: '36px',
              background: 'linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '800',
              fontSize: '0.9rem',
              color: '#0a0a0f'
            }}>VT</div>
            <span style={{ fontWeight: '600' }}>VisuTel, Inc.</span>
          </div>
          
          <div style={{ color: '#8b949e', fontSize: isMobile ? '0.75rem' : '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center', textAlign: 'center' }}>
            (c) 2026 VisuTel, Inc. All rights reserved. {!isMobile && '|'} 
            <button 
              onClick={() => scrollToSection('technology')}
              style={{
                color: '#8b949e',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.85rem',
                transition: 'color 0.2s',
                padding: 0
              }}
              onMouseOver={e => e.target.style.color = '#00d4ff'}
              onMouseOut={e => e.target.style.color = '#8b949e'}
            >Quantum Timing Infrastructure</button>
          </div>
          
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            <button 
              onClick={() => setShowPrivacyModal(true)}
              style={{
                color: '#8b949e',
                textDecoration: 'none',
                fontSize: '0.85rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                transition: 'color 0.2s'
              }}
              onMouseOver={e => e.target.style.color = '#00d4ff'}
              onMouseOut={e => e.target.style.color = '#8b949e'}
            >Privacy</button>
            <button 
              onClick={() => setShowTermsModal(true)}
              style={{
                color: '#8b949e',
                textDecoration: 'none',
                fontSize: '0.85rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                transition: 'color 0.2s'
              }}
              onMouseOver={e => e.target.style.color = '#00d4ff'}
              onMouseOut={e => e.target.style.color = '#8b949e'}
            >Terms</button>
            <button 
              onClick={() => scrollToSection('technology')}
              style={{
                color: '#8b949e',
                textDecoration: 'none',
                fontSize: '0.85rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                transition: 'color 0.2s'
              }}
              onMouseOver={e => e.target.style.color = '#00d4ff'}
              onMouseOut={e => e.target.style.color = '#8b949e'}
            >Patents</button>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '2rem'
        }} onClick={() => setShowPrivacyModal(false)}>
          <div style={{
            backgroundColor: '#0d1117',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            borderRadius: '16px',
            maxWidth: '700px',
            maxHeight: '80vh',
            overflow: 'auto',
            padding: '2rem'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#00d4ff', margin: 0 }}>Privacy Policy</h2>
              <button 
                onClick={() => setShowPrivacyModal(false)}
                style={{ background: 'none', border: 'none', color: '#8b949e', fontSize: '1.5rem', cursor: 'pointer' }}
              >-</button>
            </div>
            <div style={{ color: '#e6edf3', lineHeight: '1.7', fontSize: '0.9rem' }}>
              <p style={{ marginBottom: '1rem' }}><strong>Effective Date:</strong> January 2026</p>
              
              <h3 style={{ color: '#00d4ff', fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>1. Information We Collect</h3>
              <p style={{ marginBottom: '1rem', color: '#8b949e' }}>
                VisuTel, Inc. ("we," "our," or "us") collects information you provide directly, such as your name, email address, 
                and company information when you pre-register for our services, contact us, or subscribe to communications.
              </p>
              
              <h3 style={{ color: '#00d4ff', fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>2. How We Use Your Information</h3>
              <p style={{ marginBottom: '1rem', color: '#8b949e' }}>
                We use collected information to: provide and improve our quantum timing infrastructure services; 
                communicate about products, services, and events; respond to inquiries and provide support; 
                and comply with legal obligations.
              </p>
              
              <h3 style={{ color: '#00d4ff', fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>3. Information Sharing</h3>
              <p style={{ marginBottom: '1rem', color: '#8b949e' }}>
                We do not sell your personal information. We may share information with service providers who assist 
                our operations, or as required by law. Any sharing with government partners for infrastructure 
                deployment follows applicable regulations.
              </p>
              
              <h3 style={{ color: '#00d4ff', fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>4. Data Security</h3>
              <p style={{ marginBottom: '1rem', color: '#8b949e' }}>
                We implement industry-standard security measures including encryption and access controls. 
                As a quantum security company, we are committed to the highest standards of data protection.
              </p>
              
              <h3 style={{ color: '#00d4ff', fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>5. Your Rights</h3>
              <p style={{ marginBottom: '1rem', color: '#8b949e' }}>
                You may request access to, correction of, or deletion of your personal information by contacting 
                us at privacy@visutelinc.com. California residents have additional rights under the CCPA.
              </p>
              
              <h3 style={{ color: '#00d4ff', fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>6. Contact Us</h3>
              <p style={{ marginBottom: '0', color: '#8b949e' }}>
                For privacy-related questions, contact: privacy@visutelinc.com<br />
                VisuTel, Inc. | Quantum Timing Infrastructure
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Terms of Service Modal */}
      {showTermsModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '2rem'
        }} onClick={() => setShowTermsModal(false)}>
          <div style={{
            backgroundColor: '#0d1117',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            borderRadius: '16px',
            maxWidth: '700px',
            maxHeight: '80vh',
            overflow: 'auto',
            padding: '2rem'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#00d4ff', margin: 0 }}>Terms of Service</h2>
              <button 
                onClick={() => setShowTermsModal(false)}
                style={{ background: 'none', border: 'none', color: '#8b949e', fontSize: '1.5rem', cursor: 'pointer' }}
              >-</button>
            </div>
            <div style={{ color: '#e6edf3', lineHeight: '1.7', fontSize: '0.9rem' }}>
              <p style={{ marginBottom: '1rem' }}><strong>Effective Date:</strong> January 2026</p>
              
              <h3 style={{ color: '#00d4ff', fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>1. Acceptance of Terms</h3>
              <p style={{ marginBottom: '1rem', color: '#8b949e' }}>
                By accessing this website or pre-registering for VisuTel services, you agree to these Terms of Service 
                and our Privacy Policy. If you do not agree, please do not use our services.
              </p>
              
              <h3 style={{ color: '#00d4ff', fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>2. Description of Services</h3>
              <p style={{ marginBottom: '1rem', color: '#8b949e' }}>
                VisuTel provides quantum timing and positioning infrastructure services, including the Quantum Timing 
                and Positioning Protocol (QTPP) and related technologies. Services are subject to availability 
                and may require additional agreements.
              </p>
              
              <h3 style={{ color: '#00d4ff', fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>3. Intellectual Property</h3>
              <p style={{ marginBottom: '1rem', color: '#8b949e' }}>
                All content, patents, trademarks, and intellectual property on this site are owned by VisuTel, Inc. 
                Our patent portfolio includes innovations in quantum timing, positioning, and security technologies. 
                Unauthorized use is prohibited.
              </p>
              
              <h3 style={{ color: '#00d4ff', fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>4. Pre-Registration</h3>
              <p style={{ marginBottom: '1rem', color: '#8b949e' }}>
                Pre-registration does not guarantee service availability or pricing. We will contact pre-registered 
                users with service details and partnership opportunities as they become available.
              </p>
              
              <h3 style={{ color: '#00d4ff', fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>5. Limitation of Liability</h3>
              <p style={{ marginBottom: '1rem', color: '#8b949e' }}>
                VisuTel provides this website and information "as is" without warranties. We are not liable for 
                indirect, incidental, or consequential damages arising from use of this site or our services.
              </p>
              
              <h3 style={{ color: '#00d4ff', fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>6. Government and Critical Infrastructure</h3>
              <p style={{ marginBottom: '1rem', color: '#8b949e' }}>
                Services provided to government entities or critical infrastructure operators may be subject to 
                additional terms, security requirements, and compliance obligations under applicable laws and regulations.
              </p>
              
              <h3 style={{ color: '#00d4ff', fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>7. Contact</h3>
              <p style={{ marginBottom: '0', color: '#8b949e' }}>
                For questions about these terms, contact: legal@visutelinc.com<br />
                VisuTel, Inc. | Quantum Timing Infrastructure
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&display=swap');
        
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.6; }
          50% { transform: translateY(-20px) translateX(10px); opacity: 1; }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.1); opacity: 1; }
        }
        
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        
        html {
          scroll-behavior: smooth;
        }
        
        ::selection {
          background: rgba(0, 212, 255, 0.3);
        }
        
        /* Legislative History dropdown */
        div:has(> .leg-dropdown):hover .leg-dropdown {
          opacity: 1 !important;
          visibility: visible !important;
        }
        
        /* Mobile responsive overrides */
        @media (max-width: 768px) {
          svg {
            max-width: 100% !important;
            height: auto !important;
          }
          
          input, button {
            font-size: 16px !important; /* Prevent iOS zoom */
          }
        }
        
        /* Tablet responsive */
        @media (max-width: 1024px) and (min-width: 769px) {
          .dashboard-grid {
            grid-template-columns: 1fr !important;
          }
        }
        
        /* Smooth scrolling for touch devices */
        @supports (-webkit-touch-callout: none) {
          html {
            scroll-behavior: auto;
          }
        }
      `}</style>
    </div>
  );
};

export default VisuTelPreLaunch;
